/**
 * Module: TradeExecutionStep
 * Author: DeeTrade Team
 * Created: 2026-02-20
 * Description: Strategy Builder step-3 — configures order execution parameters
 *              for each execution node. Handles multi-stage tally verification,
 *              action-based color theming, and broker account selection.
 *
 * Key Design Decisions:
 *  - Tally engine is memoized and runs per-stage, per-exit-plane (SL/TP/Exit).
 *  - Stage complexity is computed independently for entry nodes vs exit nodes
 *    so "SINGLE" vs "STG X" badges are correct for each side.
 *  - Global exits (single SL covering all entry stages) are auto-detected.
 *  - <OrderCard> and <PositionSection> are extracted as memoized components
 *    to prevent unnecessary re-renders on sibling state changes.
 *  - Per-account order config renders as ONE ACCOUNT TAB at a time (activeAccountId), not a
 *    stacked wall of every selected account's OrderCard — 2026-07-28 FE/UX redesign. The
 *    per-account summary grid still shows every account at a glance; the tabs are for editing.
 */

import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef, memo } from 'react';
import { FiAlertCircle, FiZap, FiLayers, FiChevronDown, FiChevronRight, FiArrowUp, FiArrowDown, FiActivity, FiShield } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { Tooltip, Skeleton } from '../../../../../components/ds';
import { services } from '../../../../../services/api';
//* 10509-15 R2/R5/R6 — the ONE shared order-pricing picker (replaces this file's deleted per-node
//  Price Offset/Reference/Side controls, LiveDeploymentSettingsModal.jsx's twin).
import OrderPricingControls, { PlatformDefaultsChip } from '../../../../../components/trading/OrderPricingControls';
//* Shared helpers still used for broker display-name resolution (NOT the gating decision below).
import { buildBrokerMaps, extractBrokerList } from '../../../../../utils/brokerCapabilities';
//* order-instrument-normalization (R6.1.1/R6.1.2/R6.2/R6.3/R6.4) — class-keyed, FAIL-CLOSED
//  capability gate REPLACED brokerCapabilities.js's buildCapabilityGate for the gating decision.
//  That dead function was REMOVED in loop-back-4 (zero production callers); brokerCapabilities.js
//  now exports only buildBrokerMaps/extractBrokerList (still used here and by SmartOrderForm).
//* Defect B (#28) — shared 5-state gate-state resolver, extended onto this same module (NOT a
//  new/parallel gate) so TradeExecutionStep and SmartOrderForm distinguish LOADING /
//  UNKNOWN_INSTRUMENT / LOAD_FAILED / UNSUPPORTED / OK identically.
import {
    resolveInstrumentClassKey,
    buildClassKeyedCapabilityGate,
    fetchCapabilitiesForAccounts,
    resolveAccountGateState,
} from '../../../../../utils/instrumentClassCapabilityGate';
//* Shared, case-insensitive direction classifier (mirrors backend exit_direction) — the SINGLE
//  source the direction predicates below call, so a drifted data.type:"LONG" exit is classified
//  correctly instead of dropped by an exact-case compare (issue-strategy-graph-fe-drift, rows 2a/2c).
import { nodeDirection, DERIVATIVE_INSTRUMENT_TYPES } from '../utils/nodeUtils';

//--> 10509-15 R2/R5/R6 — priceOffset/referenceType/bidAskSide are DELETED from the per-node order
//    config. Order pricing is no longer authored per-node here; it lives at the ONE shared
//    strategy-level `/pricing` resource (OrderPricingControls + getStrategyPricing/
//    putStrategyPricing), rendered once below (not per-node, not per-stage).
const DEFAULT_ORDER = { orderType: 'LIMIT', productType: 'INTRADAY', quantity: 1, validity: 'DAY' };

// ─────────────────────────────────────────────────────────────────────────────
// Pure Helpers (outside component to avoid re-creation on every render)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * getNodeTheme — returns a Tailwind CSS class map for a given execution action.
 * Color coding:
 *  - sl        → red   (risk / stop loss)
 *  - tp        → emerald (profit taking)
 *  - exit_long / exit_short → sky (neutral close)
 *  - entry (multi-stage) → blue badge, green text
 *  - entry (single-stage) → emerald badge, green text
 *
 * @param {string} action   - node.data.action value
 * @param {boolean} isMulti - whether the route has more than 1 stage
 * @returns {Object} theme map with text, bg, dot, border, badge, tab keys
 */
const getNodeTheme = (action, isMulti) => {
    if (action === 'sl') {
        // stop-loss → loss (rose)
        return {
            text: 'text-loss',
            bg: 'bg-loss/5',
            dot: 'bg-loss',
            border: 'border-loss/20',
            badge: 'bg-loss/10 text-loss ring-1 ring-loss/30',
            tab: 'border-loss text-loss',
        };
    }
    if (action === 'tp') {
        // take-profit → profit (teal)
        return {
            text: 'text-profit',
            bg: 'bg-profit/5',
            dot: 'bg-profit',
            border: 'border-profit/20',
            badge: 'bg-profit/10 text-profit ring-1 ring-profit/30',
            tab: 'border-profit text-profit',
        };
    }
    if (action === 'exit_long' || action === 'exit_short') {
        // exit → info (blue); info is CSS-var only, so tints use color-mix
        return {
            text: 'text-[var(--badge-info-fg)]',
            bg: 'bg-[color-mix(in_oklch,var(--badge-info-dot)_6%,transparent)]',
            dot: 'bg-[var(--badge-info-dot)]',
            border: 'border-[color-mix(in_oklch,var(--badge-info-dot)_22%,transparent)]',
            badge: 'bg-[var(--badge-info-bg)] text-[var(--badge-info-fg)] ring-1 ring-[color-mix(in_oklch,var(--badge-info-dot)_30%,transparent)]',
            tab: 'border-[var(--badge-info-dot)] text-[var(--badge-info-fg)]',
        };
    }
    // Entry nodes (enter_long / enter_short) → profit (teal); multi-account variant → info (blue)
    return {
        text: 'text-profit',
        bg: 'bg-profit/5',
        dot: 'bg-profit',
        border: 'border-profit/20',
        badge: isMulti
            ? 'bg-[var(--badge-info-bg)] text-[var(--badge-info-fg)] ring-1 ring-[color-mix(in_oklch,var(--badge-info-dot)_30%,transparent)]'
            : 'bg-profit/10 text-profit ring-1 ring-profit/30',
        tab: 'border-profit text-profit',
    };
};

/** getStageCount — returns count of unique stage numbers in a node array. */
const getStageCount = (nodes) => new Set(nodes.map((n) => n.data?.stage || 1)).size;

/**
 * getShortLabel — returns a short human-readable label for a node.
 * Uses node.data.label if available; falls back to a friendly name from the action.
 */
const getShortLabel = (node) => {
    const raw = (node.data?.label || '').trim();
    if (raw) return raw;
    const action = node.data?.action || '';
    if (action === 'sl') return 'Stop Loss';
    if (action === 'tp') return 'Take Profit';
    if (action === 'exit_long' || action === 'exit_short') return 'Exit';
    if (action === 'enter_long' || action === 'enter_short') return 'Entry';
    return action;
};

/**
 * getAccountLabel — resolves a broker account's display holder name + broker name.
 * Module-scope pure helper (R1.1.2/R7) so the per-account OrderCard header and the
 * per-account summary grid never drift on how a label is resolved (broker_display_name ->
 * brokersKeyMap -> raw broker_id fallback chain lives in exactly ONE place).
 */
const getAccountLabel = (acc, brokersKeyMap) => {
    if (!acc) return { holder: 'Account', brokerName: 'BROKER' };
    const rawBrokerId = acc.broker_id;
    let brokerName = acc.broker_display_name || 'UNKNOWN BROKER';
    if (!acc.broker_display_name && brokersKeyMap && rawBrokerId) {
        brokerName = brokersKeyMap[rawBrokerId] || brokersKeyMap[String(rawBrokerId)] || String(rawBrokerId);
    }
    return { holder: acc.account_holder_name || 'Account', brokerName: String(brokerName).toUpperCase() };
};

/**
 * getNodeLogicQty — the single LOGIC-layer quantity a node's tally/balance validation sizes
 * from (`node.data.execution.quantity`, mirrored to `data.qty` at save time for the backend
 * evaluator, graph_evaluator.py:1861). Per-account-order-settings (R1.1.1) SPLITS the per-account
 * authored quantity (config.orders[nodeId][accountId].quantity, OrderCard-authored, feeds ONLY
 * R3's materialization) from this LOGIC-layer value — the tally engine intentionally keeps
 * reading THIS value (not any one account's authored quantity) so graph-balance validation stays
 * independent of which/how-many broker accounts are selected. See 02-design.md "Open questions"
 * (LOGIC-layer vs SETTINGS-layer decoupling) — flagged there as needing product sign-off that the
 * two no longer track each other automatically once an operator edits ONLY the per-account UI.
 */
const getNodeLogicQty = (node, fallback = 1) => {
    const q = node?.data?.execution?.quantity ?? node?.data?.qty;
    const n = Number(q);
    return Number.isFinite(n) && q !== '' && q != null ? n : fallback;
};

/**
 * getAccountOrderQty — per-account-order-settings BUG FIX (placement-balance tally). The
 * PLACEMENT-layer sibling of `getNodeLogicQty` above: reads a node's quantity for ONE specific
 * broker account from `executionConfig.orders[nodeId][accountId].quantity` — the value that
 * actually reaches the broker for that account — instead of the LOGIC-layer
 * `node.data.execution.quantity` (backtest sizing, intentionally decoupled, see
 * `getNodeLogicQty`'s header comment; Deepak signed off that backtest %  stays qty-invariant, so
 * this fix must NOT re-couple the two). Same signature/fallback semantics as `getNodeLogicQty` so
 * both can be passed interchangeably into `calculateTallyByStage`'s new `getQty` parameter.
 */
const getAccountOrderQty = (orders, node, accountId, fallback = 1) => {
    const q = orders?.[node?.id]?.[accountId]?.quantity;
    const n = Number(q);
    return Number.isFinite(n) && q !== '' && q != null ? n : fallback;
};

/**
 * computeExitQtyTotal — pure helper generalizing the longExitQty/shortExitQty inline useMemo
 * pattern (percent-of-entry fallback when a node has no explicit qty) so it can be reused for
 * BOTH the LOGIC-layer display totals (getNodeLogicQty) and the per-account PLACEMENT totals
 * (getAccountOrderQty), without duplicating the reduce logic per qty source.
 */
const computeExitQtyTotal = (pathNodes, entryAction, entryQtyTotal, getQty) =>
    pathNodes
        .filter((n) => n.data?.action !== entryAction)
        .reduce((sum, n) => {
            const explicit = getQty(n, null);
            return sum + (explicit != null ? explicit : (n.data?.percent ? entryQtyTotal * n.data.percent / 100 : 1));
        }, 0);

/** toTitleCase — "INTRADAY" -> "Intraday". Display-only; canonical values stay UPPERCASE on the wire. */
const toTitleCase = (s) => {
    const str = String(s || '');
    return str.length ? str[0].toUpperCase() + str.slice(1).toLowerCase() : str;
};

//--> UX redesign (2026-07-28) — human-readable labels for the manifest's classKey SEGMENT token
//    (the part after ":", e.g. "EQUITY:NSE_BSE" -> "NSE_BSE"), used ONLY in the unsupported-account
//    panel's "{Broker} supports ___" sentence. Presentation-only; mirrors (does not re-derive) the
//    server's EXCHANGE_TO_SEGMENT bucketing already applied when the manifest was built — this map
//    is the inverse for display, not a new resolution rule.
const SEGMENT_DISPLAY_LABEL = {
    NSE_BSE: 'NSE/BSE',
    NFO_BFO: 'NFO/BFO',
    MCX: 'MCX',
    CDS: 'CDS/BCD',
};

/**
 * formatSupportedSegments — given a broker's fetched classKey map (Object.keys of
 * manifestByBrokerAndClass[broker_id], e.g. ['EQUITY:NSE_BSE','FUTURE:NFO_BFO']), returns a
 * de-duplicated, human-readable list of the exchange segments that broker's manifest actually
 * declares (e.g. "NSE/BSE, NFO/BFO"). Never fabricates a segment the manifest didn't serve.
 */
const formatSupportedSegments = (classKeys) => {
    const segments = new Set(
        (classKeys || []).map((k) => String(k).split(':')[1]).filter(Boolean)
    );
    if (segments.size === 0) return 'no exchanges';
    return Array.from(segments).map((seg) => SEGMENT_DISPLAY_LABEL[seg] || seg).join(', ');
};

//--> Defect B (#28) B2/B6a — honest per-option disabled text, keyed off the shared 5-state
//    resolver (`resolveAccountGateState`) so a broker-login blip (State A) / incomplete
//    instrument (State B) / first-paint (LOADING) never renders the exact same "not supported"
//    string a genuine capability gap (State C) uses. Returns `null` for 'OK'/'UNSUPPORTED' so the
//    caller falls through to its OWN existing axis-specific "not supported" wording verbatim (S4
//    regression guard — the genuine-gap case must not change).
const gateStateTooltip = (gateState) => {
    if (gateState === 'UNKNOWN_INSTRUMENT') return 'Complete or reselect the instrument for this strategy';
    if (gateState === 'LOAD_FAILED') return "Capabilities unavailable — check the broker login/session and retry";
    if (gateState === 'LOADING') return 'Checking capabilities…';
    return null;
};

/** gateStateOptionSuffix — the short "— ___" suffix for a <select> option label. */
const gateStateOptionSuffix = (gateState) => {
    if (gateState === 'UNKNOWN_INSTRUMENT') return 'complete/reselect instrument';
    if (gateState === 'LOAD_FAILED') return 'capabilities unavailable';
    if (gateState === 'LOADING') return 'checking…';
    return 'not supported';
};

// ─────────────────────────────────────────────────────────────────────────────
// OrderCard — extracted memoized component (Review fix #3, #2)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * OrderCard — renders a single (execution node, broker account) pair's order configuration
 * form (R1.1.2 — per-account-order-settings). Memoized to prevent re-renders when sibling
 * node/account values change.
 *
 * Inputs:
 *  - node         : ReactFlow node object
 *  - isEntry      : boolean, true for entry nodes
 *  - stageCount   : number of unique stages in the node's route (entry OR exit side)
 *  - accountId    : the broker account this card edits — R1.1.2, EXACTLY one account per card
 *  - accountLabel : { holder, brokerName } — display-only, resolved via getAccountLabel
 *  - order        : this account's order config object, executionConfig.orders[nodeId][accountId]
 *  - onOrderChange : callback(nodeId, accountId, field, value)
 *  - gate         : THIS account's capability gate (R7.1/R7.2) — never the whole-set intersection
 *  - gateState    : Defect B (#28) — THIS account's `resolveAccountGateState(...)` result, parent-
 *                   computed (no new fetch). Drives B2's honest per-option messaging AND B6a's
 *                   write-path coerce guard below.
 */
const OrderCard = memo(({ node, isEntry, stageCount, accountId, accountLabel, order, onOrderChange, gate, gateState, instrument }) => {
    //--> Capability auto-correct: a saved strategy may carry an order type / product the
    //    currently-selected account's broker cannot place (e.g. MARKET or MIS on ICICI) —
    //    switch to the safe equivalents the moment THIS account's gate says no.
    //! B6a (Defect B #28, RC1 — BLOCKING) — this is the ONLY writer of these three fields, and it
    //! previously fired off the state-blind `gate` alone, so a broker-login blip (State A) or
    //! first-paint (LOADING) silently rewrote a genuinely-supported account's saved config and
    //! never restored it. Coercion now fires ONLY in State 'OK' (manifest loaded, THIS account's
    //! broker fetch succeeded, and the (broker, class) block is present) — a genuine per-axis gap
    //! (State C) still coerces; State A/LOADING/UNKNOWN_INSTRUMENT leave the saved config
    //! byte-untouched until a real capability answer exists.
    //! DEFENSE-IN-DEPTH note (QA loop-back 1, 2026-08-01): in the REAL component tree the PRIMARY
    //! control is the parent render branch (~:1742) — for every `activeAccountGateState !== 'OK'`
    //! state the parent renders CapabilityStatePanel INSTEAD of any OrderCard, so this effect
    //! never even mounts in State A/LOADING/UNKNOWN_INSTRUMENT. The `gateState !== 'OK'` early
    //! return below is therefore defense-in-depth for THAT parent gating — but it is a real,
    //! independently-load-bearing guard for a direct/isolated OrderCard mount and for any future
    //! refactor that mounts OrderCard outside the parent branch. It is pinned load-bearing by the
    //! OrderCard-UNIT RC1 negative-control tests (TradeExecutionStep.test.jsx), which mount
    //! OrderCard directly with a non-OK gateState + a fully-denying gate and assert no write —
    //! mutation-verified RED when this line is removed. The full-component RC1 tests separately
    //! document the parent mount-gating.
    useEffect(() => {
        if (gateState !== 'OK') return;
        if (!gate) return;
        if (!gate.orderTypeAllowed(order.orderType)) onOrderChange(node.id, accountId, 'orderType', 'LIMIT');
        if (!gate.productAllowed(order.productType)) onOrderChange(node.id, accountId, 'productType', 'DELIVERY');
        if (!gate.tifAllowed(order.validity)) onOrderChange(node.id, accountId, 'validity', 'IOC');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gateState, gate, order.orderType, order.productType, order.validity, node.id, accountId]);
    const stageNum = node.data?.stage || 1;
    const isMulti = stageCount > 1;
    const action = node.data?.action || (isEntry ? 'enter' : 'exit');
    const theme = getNodeTheme(action, isMulti);

    //! broker-qty-semantics loop-back iter-6 — the OrderCard lot display must AGREE with the
    //! authoritative readiness gate. The gate treats an F&O whose lot_size is unresolved (≤ 1 or
    //! missing — an expired/historic contract stub, e.g. CRUDEOIL MAR25 lot=1) as unresolved: a live
    //! order cannot be sized in lots without it. So for such an instrument the card must NOT render a
    //! confident "Places N units (N lot × 1/lot)" — that is as misleading as showing the active
    //! contract's lot. Mirror the gate's rule (DERIVATIVE + lot_size ≤ 1/missing → unresolved) and
    //! show an expired/reselect hint instead. An EQUITY with lot=1 is legitimate → normal render.
    //! Vocabulary uses the shared DERIVATIVE_INSTRUMENT_TYPES SSOT (utils/nodeUtils) — the SAME set
    //! StrategyBuilder's isFnO uses (iter-7 removed the duplicated inline literal here).
    const instrumentType = (instrument?.instrument_type || instrument?.type || '').toUpperCase();
    const isDerivative = DERIVATIVE_INSTRUMENT_TYPES.includes(instrumentType);
    //! iter-7 — the expired "reselect the active contract" hint is scoped to NON-continuous
    //! instruments. A CONTINUOUS strategy auto-rolls to the active contract (StrategyBuilder's
    //! refetch effect mirrors the readiness gate's roll), so it must never tell the user to reselect —
    //! its lot is either the rolled contract's (lot>1, no hint) or, if the roll could not resolve, an
    //! honest lots-only fallback that agrees with the gate blocking. `instrument.isContinuous` is
    //! stamped onto the OrderCard instrument by StrategyBuilder (display-only, from
    //! config.general.isContinuous).
    const lotUnresolvedFnO = !instrument?.isContinuous && isDerivative && !(Number(instrument?.lot_size) > 1);

    // Badge text
    const badgeText = isMulti ? `STG ${stageNum}` : 'SINGLE';

    // Title — strip stage numbers for singleton routes, but preserve for multi-stage
    const rawLabel = (node.data?.label || '').trim();
    let title = rawLabel
        || (isEntry ? 'Entry' : action === 'sl' ? 'Stop Loss' : action === 'tp' ? 'Take Profit' : 'Exit');
    if (!isMulti) {
        title = title.replace(/\bStage\s*\d+\b/gi, '').replace(/\s{2,}/g, ' ').trim();
    }

    return (
        <div className={`rounded-2xl border ${theme.border} overflow-hidden bg-white dark:bg-bg-2 shadow-sm hover:shadow-md transition-all duration-200`}>
            {/* R1.1.2 — per-account identity strip. Every OrderCard now renders for EXACTLY one
                (node, account) pair; this makes which account a card belongs to unambiguous at a
                glance, so two cards for the same node never look interchangeable. */}
            {accountLabel && (
                <div className="px-4 pt-2 pb-1 flex items-center gap-1.5 text-[10px] font-bold text-fg-3 uppercase tracking-wide border-b border-dashed border-[var(--divider)]">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent/70 flex-shrink-0"></span>
                    <span className="truncate">{accountLabel.holder}</span>
                    <span className="text-fg-3/60">·</span>
                    <span className="truncate">{accountLabel.brokerName}</span>
                </div>
            )}
            {/* Card Header — title left, badge right */}
            <div className={`px-4 py-2.5 ${theme.bg} flex items-center justify-between`}>
                <div className="flex flex-col">
                    <span className={`text-sm font-semibold leading-tight ${theme.text}`}>{title}</span>
                    <span className="text-[10px] text-fg-3 font-mono tracking-wide mt-0.5">{action?.toUpperCase()}</span>
                </div>
                {/* Stage badge — right aligned */}
                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[9px] font-black tracking-widest uppercase ${theme.badge}`}>
                    <span className={`w-1 h-1 rounded-full ${theme.dot} opacity-70`}></span>
                    {badgeText}
                </span>
            </div>

            <div className="p-4 bg-white dark:bg-bg-2 flex flex-col gap-4">
                {/* Order Type Tabs */}
                <div className="flex border-b border-[var(--divider)]">
                    {/*! algo-entry-null-limit-price R5 (S4, fail-CLOSED, operator decision 2a) —
                        MARKET is the PRIMARY prevention of a human authoring an unsupported manual
                        MARKET order. Unlike LIMIT/SL-M (which stay visible-but-disabled on a
                        genuine per-axis capability gap — the pre-existing pattern the `allowed`
                        check below implements), MARKET must be HIDDEN outright, never merely
                        struck-through, whenever the gate is absent/unfetched (`!gate`) OR reports
                        MARKET unsupported. `!gate` fails CLOSED here on purpose — the opposite of
                        the `!gate || ...` fail-soft idiom `allowed` still uses for LIMIT/SL-M below
                        — an unknown capability state must never SHOW a placeable-looking MARKET
                        button. `gate.orderTypeAllowed` is the SAME shared, already-fail-closed
                        instrumentClassCapabilityGate predicate the Validity control below uses
                        (buildClassKeyedCapabilityGate returns `orderTypeAllowed: () => false` on no
                        blocks). Because FT+ICICI omit MARKET from every manifest class today, this
                        filters MARKET out for both brokers. */}
                    {['MARKET', 'LIMIT', 'SL-M']
                        .filter((type) => type !== 'MARKET' || (!!gate && gate.orderTypeAllowed('MARKET')))
                        .map((type) => {
                        const allowed = !gate || gate.orderTypeAllowed(type);
                        //* R6.3/R6.4 — ⓘ tooltip (definition) sourced directly off the fetched
                        //  capability wire when allowed; disabled reason otherwise. Never a
                        //  hardcoded per-surface string.
                        const definition = gate?.getDefinition?.('order_types', type);
                        //--> Defect B (#28) B2 — State A/B/LOADING get their own honest tooltip
                        //    instead of always claiming "the broker does not permit" (that claim
                        //    is only true in State C/genuine-gap; State A/LOADING don't know yet,
                        //    State B hasn't resolved an instrument class to even check).
                        const stateTooltip = gateStateTooltip(gateState);
                        const title = allowed
                            ? (definition || undefined)
                            : (stateTooltip || `A selected account's broker does not permit ${type} orders for this instrument class`);
                        return (
                        <Tooltip
                            key={type}
                            content={title || ''}
                            placement="top"
                            className="flex-1"
                        >
                        <button
                            onClick={() => allowed && onOrderChange(node.id, accountId, 'orderType', type)}
                            disabled={!allowed}
                            className={`flex-1 py-2 text-xs font-bold uppercase tracking-wide border-b-2 transition-colors ${
                                !allowed
                                    ? 'border-transparent text-fg-3 cursor-not-allowed line-through'
                                    : order.orderType === type
                                    ? theme.tab
                                    : 'border-transparent text-fg-3 hover:text-fg-1'
                            }`}
                        >
                            {type}{allowed && definition ? ' ⓘ' : ''}
                        </button>
                        </Tooltip>
                        );
                    })}
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1">
                        <label className="text-[10px] uppercase font-bold text-fg-3">Quantity</label>
                        {/* Fix #4: Sanitize input — only accept positive integers */}
                        <input
                            type="number"
                            min="1"
                            step="1"
                            value={order.quantity}
                            onChange={(e) => {
                                const parsed = parseInt(e.target.value, 10);
                                if (!isNaN(parsed) && parsed > 0) {
                                    onOrderChange(node.id, accountId, 'quantity', parsed);
                                }
                            }}
                            className="p-2 rounded bg-bg-3 border border-[var(--divider)] text-sm font-bold text-fg-1 focus:ring-1 focus:ring-accent outline-none"
                        />
                        {/* #18 read-only order-size clarity — data.qty is written in LOTS; show the
                            resulting UNITS the broker actually receives (lots × lot_size) so the placed
                            size is unambiguous. Always shown (graceful fallback before an instrument is set). */}
                        <div className="mt-1 text-[11px] font-semibold text-fg-2" data-testid="order-size-units">
                            {/* iter-6 — an F&O whose lot is unresolved (≤ 1 / missing = expired or
                                historic contract) shows an expired/reselect hint that AGREES with the
                                readiness gate, NOT a confident "N lots × 1/lot". Checked FIRST because
                                such a row can carry a truthy lot_size=1. */}
                            {lotUnresolvedFnO ? (
                                <span className="text-loss font-normal">
                                    Lot size unresolved — this F&amp;O contract looks expired. Reselect the active contract.
                                </span>
                            ) : instrument?.lot_size ? (
                                <>
                                    Places <span className="text-accent">{(Number(order.quantity) || 0) * instrument.lot_size}</span> units{' '}
                                    <span className="font-normal text-fg-3">
                                        ({Number(order.quantity) || 0} {Number(order.quantity) === 1 ? 'lot' : 'lots'} × {instrument.lot_size}/lot)
                                    </span>
                                </>
                            ) : (
                                <>
                                    {Number(order.quantity) || 0} {Number(order.quantity) === 1 ? 'lot' : 'lots'}
                                    {!instrument && (
                                        <span className="font-normal text-fg-3">{' '}— select an instrument to see units</span>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-[10px] uppercase font-bold text-fg-3">Product</label>
                        <select
                            value={order.productType}
                            onChange={(e) => onOrderChange(node.id, accountId, 'productType', e.target.value)}
                            title={gate?.getDefinition?.('products', order.productType) || undefined}
                            className="p-2 rounded bg-bg-3 border border-[var(--divider)] text-sm font-bold text-fg-1 focus:ring-1 focus:ring-accent outline-none"
                        >
                            {/* R6.4 (2026-07-10) — canonical labels; the broker hint below carries the
                                broker's own jargon. Kept in lockstep with SmartOrderForm.jsx.
                                Defect B (#28) B2 — the disabled suffix is now state-branched
                                (`gateStateOptionSuffix`): State C (genuine gap) keeps the
                                "— not supported" string VERBATIM (S4 regression guard); State
                                A/B/LOADING get their own honest suffix instead. */}
                            <option value="INTRADAY" disabled={gate && !gate.productAllowed('INTRADAY')}>
                                {(!gate || gate.productAllowed('INTRADAY')) ? 'Intraday' : `Intraday — ${gateStateOptionSuffix(gateState)}`}
                            </option>
                            <option value="DELIVERY" disabled={gate && !gate.productAllowed('DELIVERY')}>
                                {(!gate || gate.productAllowed('DELIVERY')) ? 'Delivery' : `Delivery — ${gateStateOptionSuffix(gateState)}`}
                            </option>
                        </select>
                        {/* R6.4 — "you know this as ___" broker-label hint for the selected product */}
                        {gate?.getBrokerLabel?.('products', order.productType) && (
                            <span className="text-[9px] text-fg-3">
                                Broker calls this &quot;{gate.getBrokerLabel('products', order.productType)}&quot;
                            </span>
                        )}
                    </div>
                </div>

                {/* per-account-order-settings FE/UX redesign (item 4) — Validity renders as its
                    own clearly-labeled control (matching Quantity/Product's label+control pattern
                    above), always full-width now. 10509-15 R2/R5/R6 DELETED the Price Offset/
                    Trigger Diff input and the Reference Price/Side button-groups that used to
                    share this row — order pricing is no longer per-node/per-stage (see the ONE
                    strategy-level "Order pricing" panel this package renders once, not here). */}
                <div className="grid grid-cols-1 gap-3">
                    <div className="flex flex-col gap-1">
                        <label className="text-[10px] uppercase font-bold text-fg-3">Validity</label>
                        <div className="flex gap-1 bg-bg-3 p-1 rounded-md">
                            {['DAY', 'IOC'].map((val) => {
                                //--> R6.2/R3.7 — validity/TIF gated by the class-keyed manifest
                                //    too, per account (never advertise a TIF the account's own
                                //    broker manifest doesn't declare for this instrument class).
                                //    F11 (Stage-4 loop-back): 'VTC' dropped — formally
                                //    out-of-taxonomy, not placeable on either live adapter;
                                //    its dead FE affordance is removed, not just disabled
                                //    (mirrors SmartOrderForm.jsx's F11 change). per-account-
                                //    order-settings §7 — validity is now REAL per-account (not
                                //    authoring-only): it persists to strategy_account_
                                //    allocations.validity and reaches the broker on the LIVE
                                //    path (both ICICI and FlatTrade now declare DAY+IOC).
                                const allowed = !gate || gate.tifAllowed(val);
                                const definition = gate?.getDefinition?.('tif', val);
                                return (
                                <button
                                    key={val}
                                    onClick={() => allowed && onOrderChange(node.id, accountId, 'validity', val)}
                                    disabled={!allowed}
                                    title={allowed ? (definition || undefined) : "A selected account's broker does not permit this validity for this instrument class"}
                                    className={`flex-1 py-1.5 text-[10px] font-bold rounded transition-all ${
                                        !allowed
                                            ? 'text-fg-3 cursor-not-allowed line-through'
                                            : order.validity === val
                                            ? 'bg-white dark:bg-bg-3 shadow-sm text-fg-1'
                                            : 'text-fg-3 hover:text-fg-1'
                                    }`}
                                >
                                    {val}
                                </button>
                                );
                            })}
                        </div>
                        {/* "you know this as ___" broker-label hint for the selected validity — same
                            discipline as the Product hint above (R6.4), never hardcoded. */}
                        {gate?.getBrokerLabel?.('tif', order.validity) && (
                            <span className="text-[9px] text-fg-3">
                                Broker calls this &quot;{gate.getBrokerLabel('tif', order.validity)}&quot;
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
});
OrderCard.displayName = 'OrderCard';
//! Named export for UNIT testing ONLY (QA loop-back 1, 2026-08-01) — lets the RC1 write-path guard
//! (`if (gateState !== 'OK') return;`) be exercised in isolation, where it is the sole control, so
//! the negative-control tests are genuinely load-bearing (mutation-RED). Additive; no runtime-
//! behavior change — the app still consumes OrderCard via the module-internal reference only.
export { OrderCard };

// ─────────────────────────────────────────────────────────────────────────────
// UnsupportedAccountPanel — per-account-order-settings UX redesign, Deepak's main ask. Renders
// INSTEAD OF the order form when the active account's broker cannot trade the strategy's
// instrument class (e.g. ICICI + CRUDEOIL/MCX — ICICI's manifest has no MCX-keyed block at all).
// A disabled/all-fields-crossed-out form is dishonest here; this panel states the fact plainly
// and gives the operator exactly two ways out.
// ─────────────────────────────────────────────────────────────────────────────
const UnsupportedAccountPanel = memo(({ account, brokerName, instrument, supportedSegmentsLabel, onRemove }) => {
    const displaySymbol = instrument?.display_symbol || instrument?.symbol || 'this instrument';
    const exchange = instrument?.exchange || 'this exchange';
    return (
        <div className="rounded-xl border border-[color-mix(in_oklch,var(--color-loss)_30%,transparent)] bg-[color-mix(in_oklch,var(--color-loss)_6%,transparent)] p-6 flex flex-col items-center text-center gap-3">
            <FiAlertCircle className="text-loss" size={28} />
            <h3 className="text-base font-bold text-fg-1">
                ⚠ {brokerName} can&apos;t trade {displaySymbol} ({exchange}).
            </h3>
            <p className="text-sm text-fg-3 max-w-md">
                {brokerName} supports {supportedSegmentsLabel} — not {exchange}.
            </p>
            <p className="text-xs text-fg-3 max-w-md">
                Remove this account, or choose a supported instrument for this strategy.
            </p>
            <button
                type="button"
                onClick={onRemove}
                className="mt-2 px-4 py-2 rounded-lg bg-loss/10 text-loss text-xs font-bold uppercase tracking-wide border border-loss/30 hover:bg-loss/20 transition-colors"
            >
                Remove {account?.account_holder_name || 'account'}
            </button>
        </div>
    );
});
UnsupportedAccountPanel.displayName = 'UnsupportedAccountPanel';

// ─────────────────────────────────────────────────────────────────────────────
// CapabilityStatePanel — Defect B (#28) B1. Renders INSTEAD OF the order form for the three
// negative-but-NOT-genuinely-unsupported states (LOADING / LOAD_FAILED / UNKNOWN_INSTRUMENT).
// UnsupportedAccountPanel above is left byte-unchanged for the genuine-gap case (State C, S4
// regression guard) — this is a SIBLING panel, not a rewrite of it, so "can't trade" never
// renders for a broker-login blip or an incomplete instrument.
// ─────────────────────────────────────────────────────────────────────────────
const CapabilityStatePanel = memo(({ state, brokerName, account, onRemove }) => {
    const isLoading = state === 'LOADING';
    const isLoadFailed = state === 'LOAD_FAILED';
    const displayBroker = brokerName || 'This broker';
    const title = isLoading
        ? `Checking ${displayBroker}'s capabilities…`
        : isLoadFailed
        ? `Couldn't load ${displayBroker}'s capabilities`
        : 'Complete or reselect the instrument for this strategy';
    const body = isLoading
        ? "Hold on while we confirm what this account can trade — this is not a \"can't trade\" verdict."
        : isLoadFailed
        ? 'Check the broker login/session and retry — this account may still support this instrument; we just could not confirm it.'
        : "This strategy's instrument is missing type/exchange details, so we can't confirm what this account supports yet.";
    return (
        <div className={`rounded-xl border p-6 flex flex-col items-center text-center gap-3 ${
            isLoading
                ? 'border-[var(--divider)] bg-bg-3/50'
                : 'border-[color-mix(in_oklch,var(--warning)_30%,transparent)] bg-[color-mix(in_oklch,var(--warning)_6%,transparent)]'
        }`}>
            <FiAlertCircle className={isLoading ? 'text-fg-3' : 'text-warning'} size={28} />
            <h3 className="text-base font-bold text-fg-1">{title}</h3>
            <p className="text-sm text-fg-3 max-w-md">{body}</p>
            {!isLoading && (
                <button
                    type="button"
                    onClick={onRemove}
                    className="mt-2 px-4 py-2 rounded-lg bg-bg-3 text-fg-2 text-xs font-bold uppercase tracking-wide border border-[var(--divider)] hover:bg-bg-2 transition-colors"
                >
                    Remove {account?.account_holder_name || 'account'}
                </button>
            )}
        </div>
    );
});
CapabilityStatePanel.displayName = 'CapabilityStatePanel';

// ─────────────────────────────────────────────────────────────────────────────
// PositionSection — extracts the repeated Long/Short collapsible block (Review fix #10)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * PositionSection — renders a collapsible entry/exit card group for one position direction.
 *
 * Inputs:
 *  - direction     : 'long' | 'short'
 *  - pathNodes     : all execution nodes for this direction
 *  - entryAction   : 'enter_long' | 'enter_short'
 *  - placement     : ACTIVE account's per-direction PLACEMENT tally (entryTotal/exitTotal), or
 *                     null when no account is active — feeds the Entry/Exit Qty subtitle only
 *                     (the pass/fail balance verdict itself lives solely in ReadinessPanel, R5.7)
 *  - entryStages   : stage count for entry nodes
 *  - exitStages    : stage count for exit nodes
 *  - entryQty      : total entry quantity (for summary display)
 *  - exitQty       : total exit quantity (for summary display)
 *  - orders        : executionConfig.orders map, now { [nodeId]: { [accountId]: OrderConfig } }
 *  - onOrderChange : callback(nodeId, accountId, field, value)
 *  - selectedAccounts : broker account ids currently selected — used only for the "select an
 *                       account" empty-state message now; card RENDERING is scoped to
 *                       activeAccountId (tab-redesign, 2026-07-28)
 *  - activeAccountId  : the SINGLE account whose OrderCard renders per node (one tab bar drives
 *                       this across both PositionSections + the orphaned-nodes block)
 *  - perAccountGates  : { [accountId]: gate } — R7.1/R7.2, never a whole-set intersection
 *  - perAccountGateStates : { [accountId]: 'LOADING'|'UNKNOWN_INSTRUMENT'|'LOAD_FAILED'|
 *                       'UNSUPPORTED'|'OK' } — Defect B (#28) B6a, threaded into each OrderCard so
 *                       its coerce-write effect only fires in state 'OK'
 *  - accounts / brokersKeyMap : for per-card account-label resolution (getAccountLabel)
 *  - expanded      : boolean — is this section open
 *  - onToggle      : callback to toggle expanded state
 */
/**
 * strokeColor — returns a hex color for an SVG curve based on the exit action type.
 * SL = red, TP = emerald, neutral exit = sky blue.
 */
const strokeColor = (action) => {
    if (action === 'sl') return 'var(--color-loss)';       // stop-loss → loss
    if (action === 'tp') return 'var(--color-profit)';     // take-profit → profit
    return 'var(--badge-info-dot)';                        // exit → info blue
};

/**
 * isLongNode / isShortNode — module-level pure predicates for direction classification.
 * Defined at module scope (not inside a useCallback) so they are stable references
 * and do not need to appear in useMemo dependency arrays.
 */
const isLongNode  = (n) => nodeDirection(n) === 'long';
const isShortNode = (n) => nodeDirection(n) === 'short';

const PositionSection = memo(({
    direction, pathNodes, entryAction, placement, activeProtection,
    entryStages, exitStages, entryQty, exitQty,
    orders, onOrderChange, expanded, onToggle, wiringLines,
    selectedAccounts, activeAccountId, perAccountGates, perAccountGateStates, accounts, brokersKeyMap, instrument,
}) => {
    const isLong = direction === 'long';
    //--> When the ACTIVE account's PLACEMENT summary is provided, the header's Entry/Exit Qty
    //    subtitle reflects what will actually be PLACED for that account. Otherwise it falls back
    //    to the LOGIC/graph tally (strategy-under-construction, no account selected). This qty
    //    subtitle is the ONLY balance-adjacent thing this header renders — the pass/fail ✓/✗
    //    verdict itself lives solely in the graph-level ReadinessPanel's `sizing_balanced` item
    //    (strategy-deploy-readiness-checklist R5.7 — no second independent balance indicator here).
    const usePlacement = !!placement;
    const headerEntryQty = usePlacement ? placement.entryTotal : entryQty;
    const headerExitQty  = usePlacement ? placement.exitTotal  : exitQty;
    const entryNodes = pathNodes.filter((n) => n.data?.action === entryAction);
    const exitNodes  = pathNodes.filter((n) => n.data?.action !== entryAction);

    // Refs for the SVG overlay and each card wrapper
    const containerRef      = useRef(null);
    const elemRefs          = useRef(new Map()); // nodeId → wrapper DOM element
    // Fix #1: Cache per-nodeId ref callbacks so the identity never changes between renders.
    // Without this, React sees a new function ref every render and fires unmount+mount
    // on every node's ref callback, which triggers the recompute chain unnecessarily.
    const elemRefCallbacks  = useRef(new Map());

    // Fix #2: Store wiringLines in a ref so recompute is NOT recreated when wiringLines
    // changes reference (it comes from useMemo in the parent and is a new array every render).
    // This eliminates the cascading double-update: wiringLines→recompute→useLayoutEffect+useEffect.
    const wiringLinesRef = useRef(wiringLines);
    useEffect(() => { wiringLinesRef.current = wiringLines; }, [wiringLines]);

    // Computed SVG bezier curves: { id, d, x1, y1, x2, y2, toAction }
    const [curves, setCurves] = useState([]);
    const [svgSize, setSvgSize] = useState({ w: 0, h: 0 });

    // Fix #1: Stable per-nodeId ref callback — created once and reused across renders.
    const setElemRef = useCallback((nodeId) => {
        if (!elemRefCallbacks.current.has(nodeId)) {
            elemRefCallbacks.current.set(nodeId, (el) => {
                if (el) elemRefs.current.set(nodeId, el);
                else    elemRefs.current.delete(nodeId);
            });
        }
        return elemRefCallbacks.current.get(nodeId);
    }, []);

    // Fix #2: `expanded` only — wiringLines read via ref to break the re-creation chain.
    const recompute = useCallback(() => {
        if (!containerRef.current || !expanded) { setCurves([]); return; }
        const cRect = containerRef.current.getBoundingClientRect();
        setSvgSize({ w: cRect.width, h: cRect.height });

        const next = (wiringLinesRef.current || []).flatMap((line) => {
            const fromEl = elemRefs.current.get(line.fromId);
            const toEl   = elemRefs.current.get(line.toId);
            if (!fromEl || !toEl) return [];
            const fRect = fromEl.getBoundingClientRect();
            const tRect = toEl.getBoundingClientRect();
            // Fix #8: Store x1/y1 directly in the curve object — avoids fragile
            // path-string parsing (curve.d.split(' ')[1]) in JSX later.
            const x1 = fRect.right  - cRect.left;
            const y1 = fRect.top    + fRect.height / 2 - cRect.top;
            const x2 = tRect.left   - cRect.left;
            const y2 = tRect.top    + tRect.height / 2 - cRect.top;
            const cp = Math.max(40, Math.abs(x2 - x1) * 0.45);
            return [{
                id: line.id,
                d: `M ${x1} ${y1} C ${x1 + cp} ${y1}, ${x2 - cp} ${y2}, ${x2} ${y2}`,
                x1, y1, x2, y2,
                toAction: line.toAction,
            }];
        });
        setCurves(next);
    }, [expanded]); // wiringLines intentionally read via ref — see Fix #2 above

    // Recompute after every layout change that could move cards
    useLayoutEffect(() => { recompute(); }, [recompute]);

    // Also recompute when orders change (cards can grow/shrink)
    useEffect(() => { recompute(); }, [orders, recompute]);

    // Follow container resize (e.g. window resize, sidebar toggle)
    useEffect(() => {
        if (!containerRef.current) return;
        const obs = new ResizeObserver(recompute);
        obs.observe(containerRef.current);
        return () => obs.disconnect();
    }, [recompute]);

    return (
        <div className="rounded-xl border border-[var(--divider)] bg-white dark:bg-bg-2 overflow-hidden shadow-sm hover:shadow transition-all">
            {/* Section Header */}
            <div
                className={`flex items-center justify-between p-5 cursor-pointer bg-gradient-to-r ${
                    isLong
                        ? 'from-profit/10 to-transparent'
                        : 'from-loss/10 to-transparent'
                } dark:to-transparent border-b border-[var(--divider)] transition-colors select-none`}
                onClick={onToggle}
            >
                <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isLong
                            ? 'bg-profit/10 text-profit'
                            : 'bg-loss/10 text-loss'
                        }`}
                    >
                        {isLong ? <FiArrowUp size={20} /> : <FiArrowDown size={20} />}
                    </div>
                    <div>
                        <h3 className="font-semibold text-lg text-fg-1">
                            {isLong ? 'Long' : 'Short'} Position Strategy
                        </h3>
                        <p className="text-xs text-fg-3 mt-0.5">
                            {pathNodes.length} nodes connected • Entry Qty: {headerEntryQty} / Exit Qty: {headerExitQty}
                        </p>
                    </div>
                </div>

                {/*>>> strategy-deploy-readiness-checklist R5.7 — the per-section "Balanced"/
                    "Entry X ≠ Exit Y" pass-fail badge that used to render here is REMOVED (the
                    graph-level ReadinessPanel's sizing_balanced item is now the single surface for
                    this signal, R5.1.1). The qty subtitle above (Entry Qty/Exit Qty) and the
                    underlying tally computation (calculateTallyByStage, below) are KEPT — this
                    section still needs the per-account PLACEMENT qty numbers, just not a second
                    independent pass/fail indicator alongside the new panel. <<<*/}
                <div className="flex items-center gap-4">
                    <div className="text-fg-3 w-6 flex items-center justify-center">
                        {expanded ? <FiChevronDown size={20} /> : <FiChevronRight size={20} />}
                    </div>
                </div>
            </div>

            {/* Expanded Content */}
            {expanded && (
                <div className="p-4 md:p-6 border-t border-[var(--divider)] bg-bg-3/50">
                    {/* Relative container — SVG bezier overlay is drawn inside this */}
                    <div ref={containerRef} className="relative grid grid-cols-1 md:grid-cols-2 gap-12 items-start">

                        {/* SVG overlay — bezier curves connecting entry cards to exit cards */}
                        {curves.length > 0 && (
                            <svg
                                className="absolute inset-0 pointer-events-none hidden md:block"
                                style={{ width: svgSize.w, height: svgSize.h, overflow: 'visible' }}
                                aria-hidden="true"
                            >
                                <defs>
                                    {['sl', 'tp', 'exit'].map((type) => {
                                        const color = strokeColor(type === 'sl' ? 'sl' : type === 'tp' ? 'tp' : 'exit');
                                        return (
                                            <marker
                                                key={type}
                                                id={`arrow-${isLong ? 'long' : 'short'}-${type}`}
                                                markerWidth="6" markerHeight="6"
                                                refX="5" refY="3"
                                                orient="auto"
                                            >
                                                <path d="M 0 0 L 6 3 L 0 6 Z" fill={color} fillOpacity="0.8" />
                                            </marker>
                                        );
                                    })}
                                </defs>
                                {curves.map((curve) => {
                                    const color   = strokeColor(curve.toAction);
                                    const mType   = curve.toAction === 'sl' ? 'sl' : curve.toAction === 'tp' ? 'tp' : 'exit';
                                    const markerId = `arrow-${isLong ? 'long' : 'short'}-${mType}`;
                                    return (
                                        <g key={curve.id}>
                                            {/* Glow / shadow pass */}
                                            <path
                                                d={curve.d}
                                                fill="none"
                                                stroke={color}
                                                strokeWidth="4"
                                                strokeOpacity="0.08"
                                            />
                                            {/* Main curve */}
                                            <path
                                                d={curve.d}
                                                fill="none"
                                                stroke={color}
                                                strokeWidth="1.5"
                                                strokeOpacity="0.65"
                                                strokeDasharray="5 3"
                                                markerEnd={`url(#${markerId})`}
                                            />
                                            {/* Origin dot — Fix #8: use stored x1/y1, not parsed from path string */}
                                            <circle
                                                cx={curve.x1}
                                                cy={curve.y1}
                                                r="3" fill={color} fillOpacity="0.7"
                                            />
                                        </g>
                                    );
                                })}
                            </svg>
                        )}

                        {/* Left: Entry Execution */}
                        <div className="flex flex-col gap-4">
                            <h4 className="text-xs font-black text-fg-3 uppercase tracking-widest border-b border-[var(--divider)] pb-2">
                                Entry Execution
                            </h4>
                            {entryNodes.map((node) => (
                                <div key={node.id} ref={setElemRef(node.id)} className="flex flex-col gap-3">
                                    {selectedAccounts.length === 0 && (
                                        <div className="text-xs text-fg-3 italic p-3 bg-bg-3 dark:bg-bg-2/50 rounded-lg text-center border border-dashed border-[var(--divider)]">
                                            Select a broker account above to configure its order settings.
                                        </div>
                                    )}
                                    {/* per-account-order-settings tab redesign — render ONLY the
                                        active tab's account card per node (R1.1.2's one-card-per-
                                        (node,account) invariant still holds; presentation now
                                        shows one account at a time instead of stacking all). */}
                                    {activeAccountId && selectedAccounts.includes(activeAccountId) && (
                                        <OrderCard
                                            key={`${node.id}:${activeAccountId}`}
                                            node={node}
                                            isEntry={true}
                                            stageCount={entryStages}
                                            accountId={activeAccountId}
                                            accountLabel={getAccountLabel((accounts || []).find((a) => a.account_id === activeAccountId), brokersKeyMap)}
                                            order={orders[node.id]?.[activeAccountId] || DEFAULT_ORDER}
                                            onOrderChange={onOrderChange}
                                            gate={perAccountGates?.[activeAccountId]}
                                            gateState={perAccountGateStates?.[activeAccountId]}
                                            instrument={instrument}
                                        />
                                    )}
                                </div>
                            ))}
                            <div className="mt-2 p-4 bg-white dark:bg-bg-2 rounded-xl border border-[var(--divider)] shadow-sm">
                                <h5 className="text-[10px] font-black uppercase text-accent mb-2">Strategy Summary</h5>
                                <p className="text-xs text-fg-3 leading-relaxed">
                                    This {isLong ? 'long' : 'short'} position initiates when configured conditions are met.
                                    Up to {exitNodes.length} exit stage{exitNodes.length !== 1 ? 's' : ''} are monitored to close
                                    the position via stop loss, profit target, or reversal signals.
                                </p>
                            </div>
                        </div>

                        {/* Right: Exit Routing */}
                        <div className="flex flex-col gap-4">
                            <div className="flex items-center justify-between gap-2 border-b border-[var(--divider)] pb-2">
                                <h4 className="text-xs font-black text-fg-3 uppercase tracking-widest">
                                    Exit Routing
                                </h4>
                                {/* Per-account exit-protection (native vs synthesized) — server-decided,
                                    label rendered VERBATIM (never constructed here). Shown for the
                                    ACTIVE account so the mechanism is visible while authoring, not only
                                    at deploy. Auto-flips to "native" when a broker's live probes pass. */}
                                {activeProtection ? (
                                    <span
                                        title={activeProtection.label}
                                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide whitespace-nowrap ${
                                            activeProtection.mechanism === 'native'
                                                ? 'bg-profit/10 text-profit'
                                                : 'bg-[var(--badge-info-bg)] text-[var(--badge-info-fg)]'
                                        }`}
                                    >
                                        <FiShield size={10} />
                                        {activeProtection.mechanism === 'native' ? 'Broker-native' : 'Synthesized'}
                                    </span>
                                ) : activeAccountId ? (
                                    <span className="inline-flex items-center gap-1 text-[9px] font-medium text-fg-3 whitespace-nowrap" title="Exit-protection mechanism resolves once this account's allocation is saved.">
                                        <FiShield size={10} /> Protection shown after save
                                    </span>
                                ) : null}
                            </div>
                            {exitNodes.length === 0 ? (
                                <div className="text-sm text-fg-3 italic p-4 bg-bg-3 dark:bg-bg-2/50 rounded-lg text-center border border-dashed border-[var(--divider)]">
                                    No exit nodes mapped to this direction.
                                </div>
                            ) : (
                                <div className="flex flex-col gap-4">
                                    {exitNodes.map((node) => (
                                        <div key={node.id} ref={setElemRef(node.id)} className="flex flex-col gap-3">
                                            {activeAccountId && selectedAccounts.includes(activeAccountId) && (
                                                <OrderCard
                                                    key={`${node.id}:${activeAccountId}`}
                                                    node={node}
                                                    isEntry={false}
                                                    stageCount={exitStages}
                                                    accountId={activeAccountId}
                                                    accountLabel={getAccountLabel((accounts || []).find((a) => a.account_id === activeAccountId), brokersKeyMap)}
                                                    order={orders[node.id]?.[activeAccountId] || DEFAULT_ORDER}
                                                    onOrderChange={onOrderChange}
                                                    gate={perAccountGates?.[activeAccountId]}
                                                    gateState={perAccountGateStates?.[activeAccountId]}
                                                    instrument={instrument}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});
PositionSection.displayName = 'PositionSection';

// ─────────────────────────────────────────────────────────────────────────────
// TradeExecutionStep — Main Component
// ─────────────────────────────────────────────────────────────────────────────

const TradeExecutionStep = ({ nodes, edges = [], instrument, executionConfig, onConfigChange, onNodeExecutionChange, strategyId, onTallyChange }) => {

    const navigate = useNavigate();

    //! Use a ref for onConfigChange to avoid stale closure in useEffect (Review fix #5)
    const onConfigChangeRef = useRef(onConfigChange);
    useEffect(() => { onConfigChangeRef.current = onConfigChange; }, [onConfigChange]);

    //--> Per-account EXIT PROTECTION (native vs synthesized) surfaced at authoring, not just at
    //    deploy. SAME server single source as DeploymentSection's ProtectionTransparencySection:
    //    GET /protection-routing?strategy_id — the label is rendered VERBATIM from the server
    //    (protection_routing_display.py LABEL_NATIVE/LABEL_SYNTHETIC), never constructed here, so
    //    a broker's native-probe flip changes the string with zero UI code change. The system —
    //    not the user — decides the mechanism (there is no native/synth control anywhere). Keyed by
    //    account_id; only PERSISTED allocations return rows, so a freshly-selected-but-unsaved
    //    account simply has no row yet (badge shows "shown after save" — never a fabricated label).
    const [protectionByAccount, setProtectionByAccount] = useState({});
    useEffect(() => {
        if (!strategyId) return;
        const fetchProtection = services?.liveExitLane?.getProtectionRouting;
        if (typeof fetchProtection !== 'function') return;
        let cancelled = false;
        const req = fetchProtection(strategyId);
        if (!req || typeof req.then !== 'function') return;
        req
            .then((res) => {
                if (cancelled) return;
                const payload = res?.data?.data ?? res?.data;
                const rows = Array.isArray(payload) ? payload : (payload?.rows ?? []);
                const byId = {};
                (Array.isArray(rows) ? rows : []).forEach((r) => { if (r?.account_id) byId[r.account_id] = r; });
                setProtectionByAccount(byId);
            })
            .catch((err) => {
                if (cancelled) return;
                console.log('[DIAG] TradeExecutionStep protection-routing fetch error', { err: err?.message });
                setProtectionByAccount({});
            });
        return () => { cancelled = true; };
    }, [strategyId]);

    //--> 10509-15 R2/R5/R6 — the ONE strategy-level order-pricing authority, GET/PUT via the
    //    api.js service layer (getStrategyPricing/putStrategyPricing). Strategy-level, not
    //    per-node/per-account-tab: `pricingRowsByAccount` is keyed by account_id;
    //    `pricingDefaults` is the GET envelope's own `data.defaults` (never a hard-coded FE
    //    literal). An unsaved new strategy has no `strategyId` yet — no GET fires (R2 operator
    //    decision: show the read-only note below, never a fabricated default, since there is
    //    nothing to GET from).
    const [pricingRowsByAccount, setPricingRowsByAccount] = useState({});
    const [pricingDefaults, setPricingDefaults] = useState({});
    const [pricingLoading, setPricingLoading] = useState(false);
    //! QA H1 — the ETag used by the NEXT PUT must always be the latest one the server has told us
    //! about (a GET or a prior PUT's response), never a value captured in a stale closure. Held
    //! ONLY in a ref (never component state — nothing here renders the ETag itself) so
    //! `commitPricingChange` below always reads the current value even while called from inside an
    //! in-flight async PUT's own callback.
    const pricingEtagRef = useRef(null);
    const setPricingEtag = (v) => { pricingEtagRef.current = v; };
    //! QA H1 — serializes PUTs: while one is in flight, only the LATEST pending commit per account
    //! is kept (never fired concurrently — "queue the latest pending edit" per the brief). Ref, not
    //! state, since it must be read/written synchronously from inside the async PUT's own retry loop.
    const pricingInFlightRef = useRef(false);
    const pricingPendingRef = useRef(null); // { accountId, row } | null
    const [pricingSaving, setPricingSaving] = useState(false);
    const [pricingError, setPricingError] = useState(null);

    //--> Re-GET used both on first mount and to recover the saved values + fresh ETag after a 409.
    const fetchPricing = useCallback(async () => {
        if (!strategyId) return;
        try {
            const res = await services.strategies.getStrategyPricing(strategyId);
            const payload = res?.data?.data ?? res?.data;
            const list = Array.isArray(payload?.pricing) ? payload.pricing : [];
            const byAccount = {};
            list.forEach((r) => { if (r?.account_id) byAccount[r.account_id] = { ...r }; });
            setPricingRowsByAccount(byAccount);
            setPricingDefaults(payload?.defaults || {});
            const headerEtag = res?.headers?.etag ?? res?.headers?.ETag;
            setPricingEtag(payload?.etag ?? headerEtag ?? null);
        } catch (err) {
            console.log('[DIAG] TradeExecutionStep pricing fetch error', { err: err?.message, strategyId });
            setPricingRowsByAccount({}); setPricingDefaults({}); setPricingEtag(null);
        }
    }, [strategyId]);

    useEffect(() => {
        if (!strategyId) {
            setPricingRowsByAccount({}); setPricingDefaults({}); setPricingEtag(null);
            return undefined;
        }
        let cancelled = false;
        setPricingLoading(true);
        (async () => {
            await fetchPricing();
            if (!cancelled) setPricingLoading(false);
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [strategyId]);

    //--> Local-only edit (QA H1) — updates the draft row so every keystroke is visible, but fires
    //    NO network call. The PUT only ever happens from `commitPricingChange` below (wired to
    //    OrderPricingControls' `onCommit`, not its `onChange`).
    const handlePricingChange = useCallback((accountId, nextRow) => {
        setPricingRowsByAccount((prev) => ({
            ...prev,
            [accountId]: { ...(prev[accountId] || {}), ...nextRow, account_id: accountId },
        }));
    }, []);

    //--> QA H1 — runs the actual PUT, serialized (one in flight at a time; a commit that arrives
    //    while one is already running is remembered in `pricingPendingRef` and drained once the
    //    in-flight one settles — never fired overlapping). Always reads the ETag from the ref
    //    (latest GET/PUT response), never a value captured at render time.
    const runPricingPut = useCallback(async (accountId, row) => {
        if (pricingInFlightRef.current) {
            pricingPendingRef.current = { accountId, row };
            return;
        }
        if (!strategyId) {
            setPricingError('Not saved — strategy must be saved first.');
            return;
        }
        if (!pricingEtagRef.current) {
            setPricingError('Not saved — could not verify current pricing. Reload and retry.');
            return;
        }
        pricingInFlightRef.current = true;
        setPricingSaving(true);
        setPricingError(null);
        try {
            //! QA H1 (lead review) — PUT ONLY the committed account's row. SM upserts the listed
            //! accounts and leaves absent ones untouched, so sending the whole map from a render-time
            //! closure could silently revert another account's just-saved value when a queued
            //! commit drains (stale `pricingRowsByAccount`).
            const rows = [{
                account_id: accountId,
                entry_reference_type: row.entry_reference_type,
                entry_bid_ask_side: row.entry_bid_ask_side,
                entry_price_offset: row.entry_price_offset,
                exit_reference_type: row.exit_reference_type,
            }];
            const res = await services.strategies.putStrategyPricing(strategyId, rows, pricingEtagRef.current);
            const payload = res?.data?.data ?? res?.data;
            const headerEtag = res?.headers?.etag ?? res?.headers?.ETag;
            const freshEtag = payload?.etag ?? headerEtag ?? null;
            if (freshEtag) setPricingEtag(freshEtag);
            //--> show what the server SAVED (normalised values + pricing_source for the M12 marker).
            const saved = Array.isArray(payload?.pricing)
                ? payload.pricing.find((r) => r?.account_id === accountId) : null;
            if (saved) setPricingRowsByAccount((prev) => ({ ...prev, [accountId]: { ...saved } }));
        } catch (err) {
            const status = err?.response?.status;
            if (status === 409) {
                //--> PRICING_STALE — same display idiom as LiveDeploymentSettingsModal's own
                //    409 handling (a single inline error string); re-GET so the screen shows
                //    the SAVED values + the fresh ETag rather than the operator's now-rejected edit.
                setPricingError('Settings changed elsewhere — reload and retry.');
                //! a queued edit was made against the now-stale values — drop it; the re-GET below
                //! shows the saved record, and the operator re-applies deliberately.
                pricingPendingRef.current = null;
                await fetchPricing();
            } else if (status === 422) {
                const msg = err?.response?.data?.error?.message || err?.response?.data?.message || 'Invalid pricing values.';
                setPricingError(msg);
            } else {
                setPricingError('Not saved — could not save pricing. Reload and retry.');
            }
            console.log('[DIAG] TradeExecutionStep pricing save error', { err: err?.message, status, strategyId });
        } finally {
            pricingInFlightRef.current = false;
            setPricingSaving(false);
            const pending = pricingPendingRef.current;
            pricingPendingRef.current = null;
            if (pending) runPricingPut(pending.accountId, pending.row);
        }
    }, [strategyId, fetchPricing]);

    //--> QA H1 — wired to OrderPricingControls' `onCommit`: fires on a <select> change (one
    //    discrete choice) or the offset <input>'s blur/Enter. NEVER on every keystroke.
    const commitPricingChange = useCallback((accountId, nextRow) => {
        const merged = { ...(pricingRowsByAccount[accountId] || {}), ...nextRow, account_id: accountId };
        setPricingRowsByAccount((prev) => ({ ...prev, [accountId]: merged }));
        runPricingPut(accountId, merged);
    }, [pricingRowsByAccount, runPricingPut]);

    // 1. Identify Execution Nodes
    const executionNodes = useMemo(
        () => nodes.filter((n) => ['signal', 'position_trigger'].includes(n.type)),
        [nodes]
    );

    // Determine if any node is configured for multi-stage execution
    const isMultiStage = useMemo(
        () => executionNodes.some((n) => n.data?.execution_strategy === 'multi_stage' || n.data?.executionStrategy === 'multi_stage' || n.data?.stage > 1),
        [executionNodes]
    );

    const [accounts, setAccounts] = useState([]);
    const [brokersKeyMap, setBrokersKeyMap] = useState({});
    const [selectedAccounts, setSelectedAccounts] = useState(executionConfig.brokerAccounts || []);
    //! per-account-order-settings FE/UX tab redesign (2026-07-28, Deepak-requested) — ONE tab bar
    //  drives which SINGLE selected account's OrderCards render across both PositionSections
    //  (long/short) AND the orphaned-nodes block. Replaces the prior "stack every selected
    //  account's OrderCard" layout (a wall of ~9 near-identical cards for 3 accounts x N nodes).
    //  Lazy-initialized off `selectedAccounts` (already declared above) so the first paint never
    //  flickers through a null tab before the fallback effect below runs.
    const [activeAccountId, setActiveAccountId] = useState(() => selectedAccounts[0] || null);
    const [expandedSection, setExpandedSection] = useState('long');
    //--> order-instrument-normalization R6.1.1/R6.1.2: broker_id -> classKey -> ClassBlock, fetched
    //    from GET /brokers/{account}/capabilities (via gateway). {} until a successful fetch
    //    populates it — the fail-closed default (every axis helper reads "no block" until then).
    const [manifestByBrokerAndClass, setManifestByBrokerAndClass] = useState({});
    //--> Defect B (#28) B3 — per-broker fetch-status sibling map, `{ [broker_id]: 'ok'|'error' }`,
    //    from `fetchCapabilitiesForAccounts`'s additive `{manifests, status}` envelope. This is the
    //    signal that used to be destroyed at the fetch boundary (console.log only) — without it,
    //    `accountGateState` below cannot distinguish a fetch failure (State A) from a genuine
    //    per-class capability gap (State C); both collapsed to the same "byClass falsy" boolean.
    const [brokerLoadStatus, setBrokerLoadStatus] = useState({});
    //--> UX redesign — tracks whether the capability-manifest fetch (below) has settled at least
    //    once for the CURRENT selection, so the unsupported-account panel/chip never flashes
    //    "can't trade" during the brief window before the fetch resolves (a genuinely-missing
    //    manifest block still fails every order-form control CLOSED either way via perAccountGates
    //    — this flag only gates the PRESENTATIONAL "can't trade" panel/copy, not the gate itself).
    const [manifestLoaded, setManifestLoaded] = useState(false);

    //* Sync selected broker accounts upstream — use ref to avoid stale onConfigChange
    useEffect(() => {
        onConfigChangeRef.current({ brokerAccounts: selectedAccounts });
    }, [selectedAccounts]);

    //! per-account-order-settings tab redesign — default the active tab to the FIRST selected
    //  account; if the currently-active account gets deselected (chip click / [Remove account]
    //  toggle-off, or selectedAccounts is replaced wholesale), fall back to the first REMAINING
    //  selected account. Never auto-picks an account that isn't currently selected, and never
    //  leaves a stale activeAccountId pointing at a deselected account.
    useEffect(() => {
        setActiveAccountId((prev) => {
            if (selectedAccounts.length === 0) return null;
            if (prev && selectedAccounts.includes(prev)) return prev;
            return selectedAccounts[0];
        });
    }, [selectedAccounts]);

    // Fetch broker accounts and platform metadata on mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [accountsRes, brokersRes] = await Promise.all([
                    services.brokerAccounts.getAccounts(),
                    services.brokerPlatforms.getAllBrokers(),
                ]);

                //! Envelope-aware extraction + broker_id keying via the shared helper — GET
                //! /brokers/ wraps the list in {status, data:[...]} and items carry `broker_id`
                //! (not `id`); the old code produced an empty map on both counts. Only the
                //! display-name map is needed here now — broker_metadata (migration-008) is no
                //! longer read by this component (the class-keyed gate replaces it, R6.1.1/R6.1.2).
                const maps = buildBrokerMaps(extractBrokerList(brokersRes.data));
                setBrokersKeyMap(maps.names);

                if (accountsRes.data && accountsRes.data.length > 0) {
                    setAccounts(accountsRes.data);
                    if (!executionConfig.brokerAccounts || executionConfig.brokerAccounts.length === 0) {
                        setSelectedAccounts(accountsRes.data.map((a) => a.account_id));
                    }
                }
            } catch (err) {
                console.error('Failed to fetch broker data:', err);
            }
        };
        fetchData();
        // Intentional: runs once on mount only. executionConfig.brokerAccounts is read
        // inside the effect only as an initialization guard (skip auto-select if the
        // parent already provided pre-selected accounts). Adding executionConfig to deps
        // would reset user's account selections on every upstream config change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    //--> order-instrument-normalization R5.2.1/R6.1.2 — fetch the class-keyed capability manifest
    //    for every currently-selected account's broker; re-fetches on selection/account changes.
    //    ANY fetch failure/timeout/non-200 leaves the affected broker's key absent from the map
    //    (see fetchCapabilitiesForAccounts) -> buildClassKeyedCapabilityGate fails that broker
    //    CLOSED — never the legacy permit.
    useEffect(() => {
        let cancelled = false;
        if (selectedAccounts.length === 0) {
            setManifestByBrokerAndClass({});
            setBrokerLoadStatus({});
            setManifestLoaded(true); // nothing to load — never show a stale "checking" state
            return undefined;
        }
        setManifestLoaded(false);
        //--> Defect B (#28) B3 — `fetchCapabilitiesForAccounts` now returns the additive envelope
        //    `{ manifests, status }`; `manifests` feeds the SAME `manifestByBrokerAndClass` state
        //    every existing gate consumer already reads (unchanged shape), `status` is the NEW
        //    per-broker fetch-status sibling map `resolveAccountGateState` needs to tell a fetch
        //    failure (State A) apart from a genuine capability gap (State C).
        fetchCapabilitiesForAccounts(accounts, selectedAccounts).then(({ manifests, status }) => {
            if (!cancelled) {
                setManifestByBrokerAndClass(manifests);
                setBrokerLoadStatus(status);
                setManifestLoaded(true);
            }
        });
        return () => { cancelled = true; };
    }, [accounts, selectedAccounts]);

    //! R1.1.3 isolation regression guard — account selection changes (chip click / [Remove
    //! account]) must ONLY ever mutate `selectedAccounts`. They must NEVER prune/reset
    //! `executionConfig.orders[nodeId][accountId]` on toggle-off — a saved per-account config for a
    //! deselected-then-reselected account must re-surface as its own last-saved settings (via the
    //! `orders` useMemo below reading `legacyOrders`), never another still-selected account's
    //! current settings, and never bleed between accounts.

    //--> UX redesign (2026-07-28, merged Accounts strip) — removing an account ALWAYS deselects it,
    //    regardless of whether it is currently the active tab (unlike the chip's 3-way click below).
    //    Used by the [Remove account] button in UnsupportedAccountPanel.
    const removeAccount = useCallback((accountId) => {
        setSelectedAccounts((prev) => prev.filter((id) => id !== accountId));
    }, []);

    //--> UX redesign — ONE click gesture per chip drives BOTH selection and the active tab:
    //      - not selected       -> select it AND make it active (view it immediately)
    //      - selected, inactive -> just switch the active tab (never silently deselects a sibling
    //                               account you're merely trying to look at)
    //      - selected, active   -> deselect it (removes from the strategy). The fallback effect
    //                               above (selectedAccounts -> activeAccountId) picks the next
    //                               remaining selected account automatically.
    const handleAccountChipClick = useCallback((accountId) => {
        const isSelected = selectedAccounts.includes(accountId);
        const isActive = accountId === activeAccountId;
        if (!isSelected) {
            setSelectedAccounts((prev) => [...prev, accountId]);
            setActiveAccountId(accountId);
        } else if (!isActive) {
            setActiveAccountId(accountId);
        } else {
            setSelectedAccounts((prev) => prev.filter((id) => id !== accountId));
        }
    }, [selectedAccounts, activeAccountId]);

    //! DEFECT-2 fix — resolves to the UNKNOWN sentinel (never 'EQUITY') on a missing/blank/
    //! unrecognized instrument_type or exchange; see instrumentClassCapabilityGate.js. `instrument`
    //! is the prop this component already receives (closes the carried-but-unconsumed-prop gap —
    //! this component's gate call below was broker-only until now, per 02-design.md §3.5).
    const instrumentClass = useMemo(() => resolveInstrumentClassKey(instrument), [instrument]);

    //--> per-account-order-settings R7.1/R7.1.1/R7.1.2 — SUPERSEDES the prior single whole-set
    //    intersection gate (`capabilityGate = buildClassKeyedCapabilityGate(accounts,
    //    selectedAccounts, ...)`, decommissioned — do not re-add it as a parallel path).
    //    `buildClassKeyedCapabilityGate` is now called ONCE PER selected account (`[id]`, a
    //    singleton selection list), so account A's gate is computed from ONLY account A's own
    //    broker manifest — never intersected with any other selected account's manifest. A
    //    missing/unrecognized instrument-class manifest for one account still fails THAT
    //    account's gate closed (R7.1.2) without affecting any other account's gate.
    const perAccountGates = useMemo(
        () => Object.fromEntries(
            selectedAccounts.map((id) => [
                id,
                buildClassKeyedCapabilityGate(accounts, [id], manifestByBrokerAndClass, instrumentClass),
            ])
        ),
        [accounts, selectedAccounts, manifestByBrokerAndClass, instrumentClass]
    );

    //--> Defect B (#28) B1/B-resolver — REPLACES the state-blind `isAccountUnsupported` boolean.
    //    Resolves ONE account to one of the shared 5 states via `resolveAccountGateState`
    //    (instrumentClassCapabilityGate.js), the ONE place both this component and SmartOrderForm
    //    distinguish LOADING / UNKNOWN_INSTRUMENT (incomplete instrument) / LOAD_FAILED (broker
    //    fetch failed — State A) / UNSUPPORTED (genuine per-class capability gap — State C, the
    //    original "can't trade" case) / OK. Reuses `manifestByBrokerAndClass` + `brokerLoadStatus`,
    //    the SAME data `perAccountGates` already reads — no new fetch.
    const accountGateState = useCallback((acc) => {
        if (!acc) return 'LOADING';
        const byClass = manifestByBrokerAndClass?.[acc.broker_id] ?? manifestByBrokerAndClass?.[String(acc.broker_id)] ?? null;
        const brokerStatus = brokerLoadStatus?.[acc.broker_id] ?? brokerLoadStatus?.[String(acc.broker_id)];
        return resolveAccountGateState({ manifestLoaded, instrumentClass, brokerStatus, byClass });
    }, [manifestByBrokerAndClass, brokerLoadStatus, instrumentClass, manifestLoaded]);

    //--> Defect B (#28) B6a — per-account gate STATE map (sibling of `perAccountGates` above),
    //    threaded into each OrderCard as `gateState` so its coerce-write effect fires ONLY in
    //    state 'OK' (see OrderCard's useEffect header comment).
    const perAccountGateStates = useMemo(
        () => Object.fromEntries(
            selectedAccounts.map((id) => [id, accountGateState((accounts || []).find((a) => a.account_id === id))])
        ),
        [selectedAccounts, accounts, accountGateState]
    );

    //--> UX redesign, unsupported-account state (Deepak's main ask) — kept as the exact
    //    'UNSUPPORTED' slice of `accountGateState` so every existing consumer (placement-tally
    //    exclusion, the accounts-strip "⚠ Can't trade" chip label) keeps its original genuine-gap-
    //    only semantics; the OTHER three negative states (LOADING/UNKNOWN_INSTRUMENT/LOAD_FAILED)
    //    are handled by `accountGateState` directly at the render sites below (B1/B2/B6a), never by
    //    this boolean.
    const isAccountUnsupported = useCallback((acc) => accountGateState(acc) === 'UNSUPPORTED', [accountGateState]);

    const legacyOrders = useMemo(() => executionConfig.orders || {}, [executionConfig.orders]);

    //--> per-account-order-settings R1.1.1/R1.3/R1.3.1 — `orders` is now keyed
    //    { [nodeId]: { [accountId]: OrderConfig } }. The prior merge of `node.data.execution` into
    //    this object is DROPPED here: that field stays the separate LOGIC-layer backtest-sizing
    //    value (see getNodeLogicQty above) and no longer feeds the per-account OrderCard. R1.3.1 —
    //    a pre-feature version's `legacyOrders[nodeId]` is either absent or the OLD flat
    //    `OrderConfig` shape (an `orderType` key directly on it, no account nesting); either way
    //    every SELECTED account renders DEFAULT_ORDER here — never a fabricated divergent
    //    per-account guess built from the old shared value.
    //
    //    R1.1.3 isolation regression guard — every account that HAS a saved entry (in
    //    `legacyOrders[nodeId]`) is carried through here UNCHANGED, even one that is currently
    //    DESELECTED — only currently-SELECTED accounts additionally get DEFAULT_ORDER-merged (so a
    //    freshly-selected account with no prior save gets defaults). Rendering (OrderCard loops
    //    below) still iterates `selectedAccounts` only, so a deselected account's preserved entry
    //    here is simply not shown — but it is NOT lost, because `handleOrderChange` (below)
    //    persists via `{...orders, ...}`; if this map only ever carried currently-selected
    //    accounts, the very next unrelated field edit would silently prune every OTHER,
    //    currently-deselected account's saved settings out of `config.orders` on save.
    const orders = useMemo(() => {
        return executionNodes.reduce((acc, node) => {
            const nodeLegacy = legacyOrders[node.id];
            const isLegacyFlatShape = !!(
                nodeLegacy && typeof nodeLegacy === 'object' && !Array.isArray(nodeLegacy) && 'orderType' in nodeLegacy
            );
            const perAccount = (!isLegacyFlatShape && nodeLegacy && typeof nodeLegacy === 'object')
                ? { ...nodeLegacy }
                : {};
            selectedAccounts.forEach((accountId) => {
                perAccount[accountId] = { ...DEFAULT_ORDER, ...(perAccount[accountId] || {}) };
            });
            acc[node.id] = perAccount;
            return acc;
        }, {});
    }, [executionNodes, legacyOrders, selectedAccounts]);

    //--> per-account-order-settings R1.1.2/R1.1.3 — always writes into
    //    config.orders[nodeId][accountId][field], NEVER through onNodeExecutionChange for these
    //    fields (that callback stays wired for its own non-account-scoped LOGIC-layer caller —
    //    node.data.execution — which this handler no longer touches; see getNodeLogicQty's
    //    header comment / this package's handoff notes on that decoupling).
    const handleOrderChange = useCallback((nodeId, accountId, field, value) => {
        const nodeOrders = orders[nodeId] || {};
        const currentOrder = nodeOrders[accountId] || DEFAULT_ORDER;

        onConfigChangeRef.current({
            orders: {
                ...orders,
                [nodeId]: {
                    ...nodeOrders,
                    [accountId]: { ...currentOrder, [field]: value },
                },
            },
        });
    }, [orders]);

    //--> per-account-order-settings R6.3 — the pre-deploy drift banner (running-vs-draft
    //    allocation count) lives in <DeploymentSection>, NOT here — StrategyBuilder.jsx already
    //    threads a `draftOrdersCount` prop into it (count of (node,account) pairs the in-memory
    //    draft currently has authored), and DeploymentSection owns the "running" side's own GET.
    //    Keeping the comparison in ONE place avoids two independently-implemented drift banners
    //    drifting from each other.

    // Fix #7: isLongNode / isShortNode are now module-level pure functions (see top of file).
    // Using useCallback(fn, []) is equivalent to a module-level function but adds hook
    // bookkeeping on every render with no benefit.

    //! Fix #6: Orphaned nodes kept as array for render (.map in JSX), but we also
    //! derive an O(1) ID Set for the exclusion filter in longPathNodes/shortPathNodes,
    //! converting the previous O(n²) filter into O(n).
    const orphanedNodes = useMemo(() => {
        const edgeTargetSet = new Set(edges.map((e) => e.target));
        return executionNodes.filter((n) => !edgeTargetSet.has(n.id));
    }, [executionNodes, edges]);

    // O(1) Set for fast exclusion — do NOT use orphanedNodes.includes() anywhere below
    const orphanedNodeIds = useMemo(() => new Set(orphanedNodes.map((n) => n.id)), [orphanedNodes]);

    const longPathNodes = useMemo(
        () => executionNodes.filter((n) => isLongNode(n) && !orphanedNodeIds.has(n.id)),
        [executionNodes, orphanedNodeIds]
    );

    const shortPathNodes = useMemo(
        () => executionNodes.filter((n) => isShortNode(n) && !orphanedNodeIds.has(n.id)),
        [executionNodes, orphanedNodeIds]
    );

    // ── Tally Verification Engine (memoized) ──────────────────────────────────
    /**
     * calculateTallyByStage — validates quantity balance per execution stage.
     *
     * Rules:
     *  - Nodes are grouped by their `data.stage` (default 1).
     *  - Within each stage, SL/TP/Exit quantities are validated independently.
     *  - If an exit plane (e.g. SL) only exists on Stage 1, its quantity is
     *    matched against the TOTAL entry qty across ALL stages (Global Exit mode).
     *  - If an exit plane spans multiple stages, each stage is matched 1:1 (Stage-Specific mode).
     *
     * @param {string} entryAction - 'enter_long' | 'enter_short'
     * @param {Array}  pathNodes   - nodes belonging to this direction
     * @param {Function} [getQty]  - (node, fallback) => number. Defaults to `getNodeLogicQty` (the
     *                    LOGIC-layer / backtest-sizing source, UNCHANGED behavior — Deepak signed
     *                    off that backtest %  stays qty-invariant of broker-account settings). The
     *                    per-account-order-settings PLACEMENT-balance bug fix passes
     *                    `getAccountOrderQty` bound to one accountId instead, so the SAME
     *                    stage/global-exit grouping logic validates what actually gets PLACED for
     *                    that account — never re-coupling the two sizing sources.
     * @returns {{ totalEntryQty, isValid, imbalances, stageResults }}
     */
    const calculateTallyByStage = useCallback((entryAction, pathNodes, getQty = getNodeLogicQty) => {
        const stages = [...new Set(pathNodes.map((n) => n.data?.stage || 1))].sort((a, b) => a - b);
        const totalEntryQty = pathNodes
            .filter((n) => n.data?.action === entryAction)
            .reduce((sum, n) => sum + getQty(n), 0);

        // Fix #8: Hoist stage-specificity checks outside the per-stage loop
        const isSlStageSpecific  = pathNodes.some((n) => n.data?.action === 'sl'  && (n.data?.stage || 1) !== 1);
        const isTpStageSpecific  = pathNodes.some((n) => n.data?.action === 'tp'  && (n.data?.stage || 1) !== 1);
        const isExitStageSpecific = pathNodes.some((n) =>
            n.data?.action !== entryAction &&
            n.data?.action !== 'sl' &&
            n.data?.action !== 'tp' &&
            (n.data?.stage || 1) !== 1
        );

        const stageResults = stages.map((stageNum) => {
            const stageNodes = pathNodes.filter((n) => (n.data?.stage || 1) === stageNum);
            const entryQty = stageNodes
                .filter((n) => n.data?.action === entryAction)
                .reduce((sum, n) => sum + getQty(n), 0);

            const slNodes   = stageNodes.filter((n) => n.data?.action === 'sl');
            const tpNodes   = stageNodes.filter((n) => n.data?.action === 'tp');
            const exitNodes = stageNodes.filter((n) =>
                n.data?.action !== entryAction && n.data?.action !== 'sl' && n.data?.action !== 'tp'
            );

            // Global exit: Stage 1 exit only → must match total portfolio qty
            const slTarget   = isSlStageSpecific  ? entryQty : totalEntryQty;
            const tpTarget   = isTpStageSpecific  ? entryQty : totalEntryQty;
            const exitTarget = isExitStageSpecific ? entryQty : totalEntryQty;

            const qty = (nodeArr, target) =>
                nodeArr.reduce((sum, n) => {
                    const explicit = getQty(n, null);
                    return sum + (explicit != null ? explicit : (n.data?.percent ? target * n.data.percent / 100 : 1));
                }, 0);

            const slQty   = qty(slNodes,   slTarget);
            const tpQty   = qty(tpNodes,   tpTarget);
            const exitQty = qty(exitNodes, exitTarget);

            const slValid   = slNodes.length   === 0 || slQty   === slTarget;
            const tpValid   = tpNodes.length   === 0 || tpQty   === tpTarget;
            const exitValid = exitNodes.length === 0 || exitQty === exitTarget;

            const isStageValid = entryQty === 0 || (slValid && tpValid && exitValid);
            const imbalances = [];
            if (!slValid)   imbalances.push(`Stage ${stageNum} SL`);
            if (!tpValid)   imbalances.push(`Stage ${stageNum} TP`);
            if (!exitValid) imbalances.push(`Stage ${stageNum} Exit`);

            return { stageNum, entryQty, slQty, tpQty, exitQty, isStageValid, imbalances };
        });

        //! Direction-level reachable-exit guard (issue-strategy-graph-fe-drift §3) — aligns the SOFT
        //  FE deploy gate with the server's HARD R5 gate (ExitResolver.has_reachable_exit): a direction
        //  whose entry has NO reachable protective exit must NOT read BALANCED (the empty sl/tp/exit
        //  lists are otherwise vacuously valid, :841-843, lifting a false-positive to the deploy gate).
        //  Direction-level (true if ANY stage has an exit) so a legit multi-stage global-exit never
        //  regresses; only ever DOWNGRADES an all-empty-exit direction (conservative, safe direction).
        const hasEntry = pathNodes.some((n) => n.data?.action === entryAction);
        const hasReachableExit = pathNodes.some((n) => n.data?.action !== entryAction);
        const isValid = stageResults.every((r) => r.isStageValid) && (!hasEntry || hasReachableExit);
        const allImbalances = stageResults.flatMap((r) => r.imbalances);
        return { totalEntryQty, isValid, imbalances: allImbalances, stageResults };
        //--> per-account-order-settings R1.1.1 — tally sizing now reads getNodeLogicQty(n) (the
        //    node.data.execution/data.qty LOGIC-layer value), NOT `orders` (which is now
        //    per-account and no longer the LOGIC-layer sizing source) — no `orders` dependency.
    }, []);

    // LOGIC-layer tally (backtest sizing, node.data.execution.quantity) — UNCHANGED behavior,
    // getQty defaults to getNodeLogicQty. This is intentionally NEVER re-coupled to per-account
    // orders (Deepak signed off backtest %  stays qty-invariant of broker settings).
    const longTally  = useMemo(() => calculateTallyByStage('enter_long',  longPathNodes),  [calculateTallyByStage, longPathNodes]);
    const shortTally = useMemo(() => calculateTallyByStage('enter_short', shortPathNodes), [calculateTallyByStage, shortPathNodes]);

    const isLongTallyValid    = longTally.isValid;
    const isShortTallyValid   = shortTally.isValid;
    //--> per-account-order-settings BUG FIX — renamed from `isOverallTallyValid`: this is now ONLY
    //    the structural/LOGIC-layer graph tally (unchanged computation). The combined
    //    `isOverallTallyValid` below additionally requires the PLACEMENT tally (per selected
    //    account) to balance — see the root-cause comment on `perAccountPlacementTally`.
    const isLogicTallyValid = isLongTallyValid && isShortTallyValid && orphanedNodes.length === 0;

    // Stage count — computed per side (entry vs exit) for correct badge labeling
    const longEntryStages  = useMemo(() => getStageCount(longPathNodes.filter((n)  => n.data?.action === 'enter_long')),  [longPathNodes]);
    const longExitStages   = useMemo(() => getStageCount(longPathNodes.filter((n)  => n.data?.action !== 'enter_long')),  [longPathNodes]);
    const shortEntryStages = useMemo(() => getStageCount(shortPathNodes.filter((n) => n.data?.action === 'enter_short')), [shortPathNodes]);
    const shortExitStages  = useMemo(() => getStageCount(shortPathNodes.filter((n) => n.data?.action !== 'enter_short')), [shortPathNodes]);

    // Aggregate qty for section subtitle display (LOGIC-layer — unchanged)
    const longEntryQty  = longTally.totalEntryQty;
    const longExitQty   = useMemo(
        () => computeExitQtyTotal(longPathNodes, 'enter_long', longEntryQty, getNodeLogicQty),
        [longPathNodes, longEntryQty]
    );
    const shortEntryQty = shortTally.totalEntryQty;
    const shortExitQty  = useMemo(
        () => computeExitQtyTotal(shortPathNodes, 'enter_short', shortEntryQty, getNodeLogicQty),
        [shortPathNodes, shortEntryQty]
    );

    //--> per-account-order-settings BUG FIX (root cause: Deepak set exit=2/entry=1 per-account and
    //    the tally still read BALANCED) — the tally engine sized from `getNodeLogicQty` (the
    //    node.data.execution/data.qty LOGIC-layer value), fully decoupled from the per-account
    //    OrderCard qty (`config.orders[nodeId][accountId].quantity`), so a per-account edit never
    //    moved the balance. This PLACEMENT tally validates what is ACTUALLY placed per account —
    //    for each selected account whose broker CAN trade this instrument class (unsupported
    //    accounts place nothing and are excluded), sum(entry qty) must equal sum(exit qty) using
    //    the SAME stage/global-exit grouping rules as the LOGIC tally above, just re-sourced via
    //    `getAccountOrderQty`. Does NOT touch/replace the LOGIC tally (backtest sizing stays
    //    independent, per Deepak's sign-off — see `getNodeLogicQty`'s header comment).
    const perAccountPlacementTally = useMemo(() => {
        const result = {};
        selectedAccounts.forEach((accountId) => {
            const acc = accounts.find((a) => a.account_id === accountId);
            const unsupported = isAccountUnsupported(acc);
            if (unsupported) {
                // Unsupported accounts place nothing — excluded from the balance requirement
                // entirely (never contribute a false imbalance, never a false balance).
                result[accountId] = {
                    isValid: true, unsupported: true, entryTotal: 0, exitTotal: 0,
                    long:  { isValid: true, entryTotal: 0, exitTotal: 0 },
                    short: { isValid: true, entryTotal: 0, exitTotal: 0 },
                };
                return;
            }
            const getQty = (node, fallback = 1) => getAccountOrderQty(orders, node, accountId, fallback);
            const long  = calculateTallyByStage('enter_long',  longPathNodes,  getQty);
            const short = calculateTallyByStage('enter_short', shortPathNodes, getQty);
            const longExitTotal  = computeExitQtyTotal(longPathNodes,  'enter_long',  long.totalEntryQty,  getQty);
            const shortExitTotal = computeExitQtyTotal(shortPathNodes, 'enter_short', short.totalEntryQty, getQty);
            result[accountId] = {
                isValid: long.isValid && short.isValid,
                unsupported: false,
                entryTotal: long.totalEntryQty + short.totalEntryQty,
                exitTotal: longExitTotal + shortExitTotal,
                //--> per-direction breakdown so each PositionSection header badge/subtitle can
                //    reflect the ACTIVE account's PLACEMENT balance for ITS direction — keeping the
                //    section card in lockstep with the top "Position Verification Engine" banner
                //    (Deepak: the section still read "Balanced/Exit Qty 1" while the top read
                //    "Entry 1 ≠ Exit 3"). Same source, no second computation path.
                long:  { isValid: long.isValid,  entryTotal: long.totalEntryQty,  exitTotal: longExitTotal },
                short: { isValid: short.isValid, entryTotal: short.totalEntryQty, exitTotal: shortExitTotal },
            };
        });
        return result;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAccounts, accounts, orders, longPathNodes, shortPathNodes, calculateTallyByStage, manifestByBrokerAndClass, instrumentClass, manifestLoaded]);

    //--> Overall placement validity: vacuously true when no accounts are selected (preserves the
    //    pre-fix LOGIC-only gate for a strategy still under construction, before any broker account
    //    is chosen) or when every selected+supported account is balanced.
    const isPlacementTallyValid = useMemo(
        () => selectedAccounts.every((id) => perAccountPlacementTally[id]?.isValid !== false),
        [selectedAccounts, perAccountPlacementTally]
    );

    //! per-account-order-settings BUG FIX — the deploy-gate signal now requires BOTH the
    //! structural/LOGIC-layer graph tally AND the per-account PLACEMENT tally to hold. This is the
    //! value Deepak's bug report needed: entry=1/exit=2 per account now flips this to false instead
    //! of silently reading BALANCED.
    const isOverallTallyValid = isLogicTallyValid && isPlacementTallyValid;

    //--> Lift tally validity to parent for deployment gate
    useEffect(() => {
        onTallyChange?.(isOverallTallyValid);
    }, [isOverallTallyValid, onTallyChange]);

    /**
     * buildWiringLines — builds entry→exit connection descriptors for SVG bezier rendering.
     *
     * Strategy:
     *  1. Try direct graph edges (entry node → exit node via graph edges).
     *  2. Fall back to full cross-product (every entry fans into every exit),
     *     because at execution time all entries implicitly activate all exits.
     *
     * Returns: [{ id, fromId, toId, fromLabel, fromStage, toLabel, toAction, toStage }]
     * The fromId/toId are React Flow node IDs used for DOM ref lookup.
     */
    const buildWiringLines = useCallback((entryNodes, exitNodes) => {
        if (!entryNodes.length || !exitNodes.length) return [];

        const entryIds = new Set(entryNodes.map((n) => n.id));
        const exitIds  = new Set(exitNodes.map((n) => n.id));
        const entryMap = new Map(entryNodes.map((n) => [n.id, n]));
        const exitMap  = new Map(exitNodes.map((n) => [n.id, n]));

        const directLinks = edges
            .filter((e) => entryIds.has(e.source) && exitIds.has(e.target))
            .map((e) => ({ from: entryMap.get(e.source), to: exitMap.get(e.target) }))
            .filter((l) => l.from && l.to);

        const source = directLinks.length > 0
            ? directLinks
            : entryNodes.flatMap((entry) => exitNodes.map((exit) => ({ from: entry, to: exit })));

        const seen = new Set();
        return source
            .map(({ from, to }) => {
                const id = `${from.id}_${to.id}`;
                if (seen.has(id)) return null;
                seen.add(id);
                return {
                    id,
                    // DOM lookup IDs
                    fromId:    from.id,
                    toId:      to.id,
                    // Labels for tooltip / legend (not used in SVG render, but useful for debugging)
                    fromLabel: getShortLabel(from),
                    fromStage: from.data?.stage || 1,
                    toLabel:   getShortLabel(to),
                    toAction:  to.data?.action || '',
                    toStage:   to.data?.stage  || 1,
                };
            })
            .filter(Boolean)
            .sort((a, b) => (a.fromStage - b.fromStage) || (a.toStage - b.toStage));
    }, [edges]);

    const longWiringLines = useMemo(() => {
        const entries = longPathNodes.filter((n) => n.data?.action === 'enter_long');
        const exits   = longPathNodes.filter((n) => n.data?.action !== 'enter_long');
        return buildWiringLines(entries, exits);
    }, [longPathNodes, buildWiringLines]);

    const shortWiringLines = useMemo(() => {
        const entries = shortPathNodes.filter((n) => n.data?.action === 'enter_short');
        const exits   = shortPathNodes.filter((n) => n.data?.action !== 'enter_short');
        return buildWiringLines(entries, exits);
    }, [shortPathNodes, buildWiringLines]);

    //--> UX redesign — the merged Accounts strip's per-chip order summary ("LIMIT · Intraday · 1")
    //    reads the FIRST entry node's order for that account, mirroring the decommissioned
    //    "Per-account summary" grid's own entryNode resolution exactly (same fallback chain).
    const summaryEntryNode = useMemo(
        () => executionNodes.find((n) => String(n.data?.action || '').toLowerCase().startsWith('enter')) || executionNodes[0],
        [executionNodes]
    );

    const activeAccount = useMemo(
        () => accounts.find((a) => a.account_id === activeAccountId) || null,
        [accounts, activeAccountId]
    );
    //--> Defect B (#28) B1 — the ACTIVE account's full 5-state resolution, drives the render
    //    branch below (panel variant selection). `isActiveAccountUnsupported` stays the exact
    //    'UNSUPPORTED' slice, unchanged semantics for every OTHER consumer below.
    const activeAccountGateState = activeAccountId ? accountGateState(activeAccount) : null;
    const isActiveAccountUnsupported = activeAccountGateState === 'UNSUPPORTED';
    const activePlacement = activeAccountId ? perAccountPlacementTally[activeAccountId] : null;
    //--> The ACTIVE account's per-direction PLACEMENT summary, fed to each PositionSection header so
    //    the section's Balanced/Imbalance badge + "Entry/Exit Qty" subtitle match the top banner
    //    (both read the SAME placement source). Null when no account is active or it can't trade
    //    this instrument — the header then falls back to the LOGIC/graph tally, which is the right
    //    signal for a strategy still under construction with no broker account chosen.
    const showActivePlacement = !!(activeAccountId && !isActiveAccountUnsupported && activePlacement);
    const activeLongPlacement  = showActivePlacement ? activePlacement.long  : null;
    const activeShortPlacement = showActivePlacement ? activePlacement.short : null;
    //--> The ACTIVE account's exit-protection row (server-sourced, label verbatim), shown in each
    //    PositionSection's Exit Routing header. Null when unsupported (no exits placed) or when the
    //    account has no persisted allocation row yet.
    const activeProtection = (activeAccountId && !isActiveAccountUnsupported)
        ? (protectionByAccount[activeAccountId] || null)
        : null;
    //--> "the exchanges its manifest declares" sentence — derived from the ACTIVE account's own
    //    fetched classKey map (never a hardcoded broker->exchange table).
    const activeBrokerName = activeAccount ? getAccountLabel(activeAccount, brokersKeyMap).brokerName : '';
    const activeSupportedSegments = useMemo(() => {
        if (!activeAccount) return '';
        const byClass = manifestByBrokerAndClass?.[activeAccount.broker_id] ?? manifestByBrokerAndClass?.[String(activeAccount.broker_id)] ?? null;
        return formatSupportedSegments(byClass ? Object.keys(byClass) : []);
    }, [activeAccount, manifestByBrokerAndClass]);

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div className="max-w-4xl mx-auto flex flex-col gap-6">
            {/* Header */}
            <div className="flex items-center justify-between pb-4 border-b border-[var(--divider)]">
                <div>
                    <h2 className="text-xl font-black text-fg-1 flex items-center gap-2">
                        <FiZap className="text-accent" /> Trade Execution Sizing
                    </h2>
                    <p className="text-sm text-fg-3 mt-1">
                        Configure quantities, order types, and broker sizing based on your strategy's signals.
                    </p>
                </div>
                {isMultiStage ? (
                    <div className="px-3 py-1 rounded-full bg-[var(--badge-info-bg)] text-[var(--badge-info-fg)] text-xs font-bold flex items-center gap-1 border border-[color-mix(in_oklch,var(--badge-info-dot)_25%,transparent)]">
                        <FiLayers /> Multi-Stage
                    </div>
                ) : (
                    <div className="px-3 py-1 rounded-full bg-bg-3 text-fg-1 dark:bg-bg-2 text-xs font-bold border border-[var(--divider)]">
                        Single Entry
                    </div>
                )}
            </div>

            {/*>>> strategy-deploy-readiness-checklist R5.7 (C1 fix-up) — the "Position Verification
                Engine" banner that used to render here (Entry X = Exit Y ✓ / "All position chains
                perfectly balanced!", driven by `isOverallTallyValid`/`activePlacement.isValid`) is
                REMOVED. It independently duplicated the graph-level ReadinessPanel's `sizing_balanced`
                REQUIRED item — R5.7's AC forbids two independent ✓/✗ or Balanced/Imbalanced
                indicators describing overlapping information. The orphaned-nodes count line that
                lived in this banner is also gone; it was purely redundant with the "Unlinked Orders"
                panel rendered further below (same `orphanedNodes` array, full per-node detail there).
                `isOverallTallyValid`/`isLongTallyValid`/`isShortTallyValid` computations are KEPT —
                they still feed `onTallyChange` (deploy-gate lift) and `isLogicTallyValid`; only this
                render surface is deleted. <<<*/}

            {/* Accounts strip (per-account-order-settings UX redesign, 2026-07-28, Deepak-directed)
                — merges the prior "Target Broker Accounts" selection grid + "Per-account summary"
                grid + tab bar into ONE strip: one chip per AVAILABLE account. A selected chip is
                accent-highlighted (the ACTIVE one more so); an unselected chip renders muted/dashed
                ("add" affordance). Clicking a chip both selects it (if not already) and makes it
                the active tab — see `handleAccountChipClick`'s header comment for the exact 3-way
                click semantics. Each chip shows: holder · broker, a compact entry-order summary
                (or "⚠ can't trade" if unsupported), and a ✓/⚠ per-account placement-balance
                indicator (BUG FIX — sourced from `perAccountPlacementTally`, the actual per-account
                authored quantities, not the LOGIC-layer graph tally). */}
            <div className="bg-white dark:bg-bg-2 p-5 rounded-xl border border-[var(--divider)] shadow-sm">
                <div className="flex items-center justify-between mb-1">
                    <h4 className="text-xs uppercase font-black text-fg-2 tracking-widest">Execution Plan</h4>
                    <span className="text-[10px] font-bold text-fg-3">
                        {selectedAccounts.length} of {accounts.length} account{accounts.length === 1 ? '' : 's'} selected
                    </span>
                </div>
                <p className="text-[10px] text-fg-3 mb-3">
                    Every selected account below places this strategy&apos;s orders. Click a card to add it or open its settings — the highlighted card is the one you&apos;re editing underneath.
                </p>
                {/* Overall plan = the row of per-account cards (each one IS that account's summary:
                    order line + exit-protection + balance). Solid = in the plan; the ACCENT-RINGED
                    card is the one being edited below (tied to the "Editing" band under this strip);
                    dashed/ghosted = not yet added. */}
                <div className="flex flex-wrap gap-x-3 gap-y-6">
                    {accounts.length === 0 && <span className="text-xs text-fg-3">Loading accounts...</span>}
                    {accounts.map((acc) => {
                        const isSelected = selectedAccounts.includes(acc.account_id);
                        const isActive   = isSelected && acc.account_id === activeAccountId;
                        const { holder, brokerName } = getAccountLabel(acc, brokersKeyMap);
                        const unsupported = isSelected && isAccountUnsupported(acc);
                        const entryOrder = orders[summaryEntryNode?.id]?.[acc.account_id] || DEFAULT_ORDER;
                        const placement = perAccountPlacementTally[acc.account_id];
                        const prot = protectionByAccount[acc.account_id];
                        const shortName = (acc.account_holder_name || 'Unknown').substring(0, 2).toUpperCase();
                        return (
                            <button
                                key={acc.account_id}
                                type="button"
                                aria-pressed={isSelected}
                                data-active={isActive ? 'true' : 'false'}
                                aria-label={`${holder} · ${brokerName}${isActive ? ' (editing)' : ''}`}
                                onClick={() => handleAccountChipClick(acc.account_id)}
                                className={`relative text-left flex items-start gap-2.5 px-3.5 py-2.5 rounded-xl border transition-all min-w-[212px] ${
                                    isActive
                                        ? 'border-accent/60 bg-accent/[0.07] shadow-md'
                                        : isSelected
                                        ? 'border-[var(--divider)] bg-white dark:bg-bg-2 shadow-sm hover:border-accent/40'
                                        : 'border-dashed border-[var(--divider)] bg-transparent opacity-70 hover:opacity-100 hover:border-accent/40'
                                }`}
                            >
                                <div className={`w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-[11px] font-black ${
                                    isSelected ? 'bg-accent text-white'
                                        : 'bg-bg-3 text-fg-3 border border-dashed border-[var(--divider)]'
                                }`}>
                                    {isSelected ? shortName : '+'}
                                </div>
                                <div className="flex flex-col overflow-hidden leading-tight min-w-0 gap-0.5">
                                    <span className={`text-xs font-black uppercase truncate ${isSelected ? 'text-fg-1' : 'text-fg-2'}`}>
                                        {holder}
                                    </span>
                                    <span className="text-[9px] font-bold text-fg-3 uppercase tracking-wide truncate">{brokerName}</span>
                                    {isSelected ? (
                                        unsupported ? (
                                            <span className="text-[10px] font-bold text-loss mt-0.5">⚠ Can&apos;t trade this instrument</span>
                                        ) : (
                                            <>
                                                <span className="text-[10px] font-mono text-fg-2 mt-0.5 flex items-center gap-1 whitespace-nowrap">
                                                    {entryOrder.orderType} · {toTitleCase(entryOrder.productType)} · {entryOrder.quantity}
                                                    {placement && (
                                                        placement.isValid
                                                            ? <span className="text-profit font-bold" title="Entry/exit balanced">✓</span>
                                                            : <span className="text-loss font-bold" title="Entry/exit mismatch">⚠</span>
                                                    )}
                                                </span>
                                                {prot && (
                                                    <span
                                                        title={prot.label}
                                                        className={`inline-flex items-center gap-1 mt-0.5 text-[9px] font-bold uppercase tracking-wide ${
                                                            prot.mechanism === 'native' ? 'text-profit' : 'text-[var(--badge-info-fg)]'
                                                        }`}
                                                    >
                                                        <FiShield size={9} />
                                                        {prot.mechanism === 'native' ? 'Broker-native' : 'Synthesized'}
                                                    </span>
                                                )}
                                            </>
                                        )
                                    ) : (
                                        <span className="text-[10px] font-bold text-fg-3 mt-0.5">Add account</span>
                                    )}
                                </div>
                                {isActive && (
                                    <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-accent text-white text-[8px] font-black uppercase tracking-widest shadow-sm whitespace-nowrap">
                                        Editing ▾
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* 10509-15 R2/R5/R6 — ONE strategy-level order-pricing panel (replaces the deleted
                per-node Price Offset/Reference/Side controls). Applies to every entry and exit of
                this strategy — editing it here edits the ACTIVE account's row only (same one-tab-
                at-a-time editing model as the per-account order settings above); switching the
                active-account tab switches which row is shown. An unsaved new strategy (no
                `strategyId` yet) has nothing to GET from — shows the platform defaults READ-ONLY
                with a note, never a fabricated/hard-coded default (operator decision, R2). */}
            {selectedAccounts.length > 0 && activeAccountId && (
                <div className="bg-white dark:bg-bg-2 p-4 rounded-xl border border-[var(--divider)] shadow-sm">
                    <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                                <h4 className="text-xs uppercase font-black text-fg-2 tracking-widest">Order pricing</h4>
                                {/*! Iteration 4 — ONE combined chip, right next to the title, replacing
                                    the two per-section "Platform default" pills OrderPricingControls
                                    used to render internally. */}
                                <PlatformDefaultsChip value={pricingRowsByAccount[activeAccountId]} defaults={pricingDefaults} />
                            </div>
                            {/*! QA H1 — same inline-error idiom as LiveDeploymentSettingsModal's own
                                `error` span (text-loss, shown verbatim, never silently swallowed) —
                                moved into the header area so it reads as a property of the panel,
                                not a stray line floating above the controls. */}
                            {pricingError && (
                                <p className="text-xs text-loss mt-1">{pricingError}</p>
                            )}
                        </div>
                        {pricingSaving && (
                            <span className="text-[10px] font-bold text-fg-3 uppercase tracking-wide shrink-0">Saving…</span>
                        )}
                    </div>
                    {!strategyId ? (
                        <p className="text-xs text-fg-3">Editable after first save.</p>
                    ) : pricingLoading ? (
                        <div className="space-y-3">
                            <div className="space-y-2">
                                <Skeleton w="30%" h={11} />
                                <Skeleton h={32} />
                            </div>
                            <div className="space-y-2 pt-2 border-t border-[var(--divider)]">
                                <Skeleton w="20%" h={11} />
                                <Skeleton w="50%" h={32} />
                            </div>
                        </div>
                    ) : (
                        <OrderPricingControls
                            value={pricingRowsByAccount[activeAccountId] || {}}
                            defaults={pricingDefaults}
                            onChange={(next) => handlePricingChange(activeAccountId, next)}
                            onCommit={(next) => commitPricingChange(activeAccountId, next)}
                            disabled={pricingSaving}
                        />
                    )}
                </div>
            )}

            {/* Orders Layout — Position Sections, gated by the ACTIVE account's own perAccountGate.
                Defect B (#28) B1 — branches on the full 5-state `activeAccountGateState`, not just
                the genuine-gap boolean: State C (UNSUPPORTED) renders the EXISTING
                UnsupportedAccountPanel byte-unchanged (S4 regression guard); State A/B/LOADING
                render the sibling CapabilityStatePanel with its own honest copy — NEVER the
                "can't trade"/"supports no exchanges" strings that used to render for all four. */}
            {selectedAccounts.length > 0 && activeAccountGateState && activeAccountGateState !== 'OK' ? (
                activeAccountGateState === 'UNSUPPORTED' ? (
                    <UnsupportedAccountPanel
                        account={activeAccount}
                        brokerName={activeBrokerName}
                        instrument={instrument}
                        supportedSegmentsLabel={activeSupportedSegments}
                        onRemove={() => removeAccount(activeAccountId)}
                    />
                ) : (
                    <CapabilityStatePanel
                        state={activeAccountGateState}
                        brokerName={activeBrokerName}
                        account={activeAccount}
                        onRemove={() => removeAccount(activeAccountId)}
                    />
                )
            ) : executionNodes.length === 0 ? (
                <div className="p-8 text-center text-fg-3 bg-white dark:bg-bg-2 rounded-xl border border-[var(--divider)]">
                    No entry or exit signaling nodes found in your strategy logic. Please add Execution nodes first.
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    {/* "Editing" band — ties the position sections below to the highlighted account
                        card above (Deepak: "the bottom section is part of the selected item").
                        Same identity (avatar + holder + broker) and accent as the active chip, so
                        the eye connects the two without a fragile physical connector across a
                        wrapped chip row. */}
                    {activeAccount && (
                        <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-accent/[0.07] border border-accent/25">
                            <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-[10px] font-black bg-accent text-white">
                                {(activeAccount.account_holder_name || 'Unknown').substring(0, 2).toUpperCase()}
                            </div>
                            <div className="flex flex-col leading-tight min-w-0">
                                <span className="text-[9px] font-bold uppercase tracking-widest text-accent">Editing order settings for</span>
                                <span className="text-sm font-black text-fg-1 truncate">
                                    {getAccountLabel(activeAccount, brokersKeyMap).holder}
                                    <span className="text-fg-3 font-bold"> · {getAccountLabel(activeAccount, brokersKeyMap).brokerName}</span>
                                </span>
                            </div>
                            <span className="ml-auto text-[10px] font-medium text-fg-3 hidden sm:block">
                                Changes below apply to this account only. Switch accounts with the cards above.
                            </span>
                        </div>
                    )}

                    {/* Long Position */}
                    {longPathNodes.length > 0 && (
                        <PositionSection
                            direction="long"
                            pathNodes={longPathNodes}
                            entryAction="enter_long"
                            placement={activeLongPlacement}
                            activeProtection={activeProtection}
                            entryStages={longEntryStages}
                            exitStages={longExitStages}
                            entryQty={longEntryQty}
                            exitQty={longExitQty}
                            orders={orders}
                            onOrderChange={handleOrderChange}
                            selectedAccounts={selectedAccounts}
                            activeAccountId={activeAccountId}
                            perAccountGates={perAccountGates}
                            perAccountGateStates={perAccountGateStates}
                            accounts={accounts}
                            brokersKeyMap={brokersKeyMap}
                            instrument={instrument}
                            wiringLines={longWiringLines}
                            expanded={expandedSection === 'long'}
                            onToggle={() => setExpandedSection((s) => s === 'long' ? null : 'long')}
                        />
                    )}

                    {/* Short Position */}
                    {shortPathNodes.length > 0 && (
                        <PositionSection
                            direction="short"
                            pathNodes={shortPathNodes}
                            entryAction="enter_short"
                            placement={activeShortPlacement}
                            activeProtection={activeProtection}
                            entryStages={shortEntryStages}
                            exitStages={shortExitStages}
                            entryQty={shortEntryQty}
                            exitQty={shortExitQty}
                            orders={orders}
                            onOrderChange={handleOrderChange}
                            selectedAccounts={selectedAccounts}
                            activeAccountId={activeAccountId}
                            perAccountGates={perAccountGates}
                            perAccountGateStates={perAccountGateStates}
                            accounts={accounts}
                            brokersKeyMap={brokersKeyMap}
                            instrument={instrument}
                            wiringLines={shortWiringLines}
                            expanded={expandedSection === 'short'}
                            onToggle={() => setExpandedSection((s) => s === 'short' ? null : 'short')}
                        />
                    )}

                    {/* Orphaned Nodes — Fix #6: pass stageCount=1 explicitly */}
                    {orphanedNodes.length > 0 && (
                        <div className="rounded-xl border border-[color-mix(in_oklch,var(--warning)_30%,transparent)] bg-[color-mix(in_oklch,var(--warning)_8%,transparent)] overflow-hidden mt-4">
                            <div className="p-4 border-b border-[color-mix(in_oklch,var(--warning)_20%,transparent)] text-warning text-sm font-bold flex items-center gap-2">
                                <FiAlertCircle />
                                Unlinked Orders (Please attach to an Entry node)
                            </div>
                            <div className="p-4 space-y-4">
                                {orphanedNodes.map((node) => (
                                    <div key={node.id} className="flex flex-col gap-3">
                                        {activeAccountId && selectedAccounts.includes(activeAccountId) && (
                                            <OrderCard
                                                key={`${node.id}:${activeAccountId}`}
                                                node={node}
                                                isEntry={false}
                                                stageCount={1}
                                                accountId={activeAccountId}
                                                accountLabel={getAccountLabel(accounts.find((a) => a.account_id === activeAccountId), brokersKeyMap)}
                                                order={orders[node.id]?.[activeAccountId] || DEFAULT_ORDER}
                                                onOrderChange={handleOrderChange}
                                                gate={perAccountGates[activeAccountId]}
                                                gateState={perAccountGateStates[activeAccountId]}
                                                instrument={instrument}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}


        </div>
    );
};

export default TradeExecutionStep;
